/*
  PA Personalization Drawer
  Asset: assets/pa-personalization-drawer.js

  Scope
  - Initializes the side drawer rendered by snippets/pa-personalization-drawer.liquid.
  - Preserves native UploadKit properties for micro-photo, dual-micro-photo and laser-photo flows.
  - Handles text, initial, coordinates, dynamic text, variant-based plate engraving and optional back-side laser engraving.
  - Handles the TeeInBlue star-map flow by hydrating TeeInBlue's stored customization data,
    creating the customization through the TeeInBlue SDK, then adding the Shopify cart item
    with the generated private order properties.

  Maintenance rules
  - Do not rebuild UploadKit URLs or rename UploadKit-generated properties.
  - Do not use broad document.body MutationObservers for TeeInBlue.
  - For star maps, keep the TeeInBlue storage payload keys aligned with the campaign:
    template key "982287", layer key "layer-835577", template id "template-550818".
  - Private TeeInBlue properties kept for production are:
    _customization_id, _customization_image and _tib_design_link_1.
  - v1.4.2 keeps cart payloads stable while replacing blocking alerts with drawer-native errors.
  - v1.5 adds addon information bottom sheets without changing addon cart payloads.

  Internal map
  1. Constants, selectors and shared helpers.
  2. Section lifecycle, scoped listeners, timers and cleanup.
  3. Product form and variant synchronization.
  4. Shared validation and UploadKit property helpers.
  5. Coordinates, star-map search and TeeInBlue integration.
  6. Laser, dynamic text, variant plates, fingerprint and Morse collectors.
  7. Drawer, modals, keyboard handling and totals.
  8. Upload validation, cart item building and submit flow.
*/
(function () {
  'use strict';

  /* --------------------------------------------------------------------------
    1. Constants, selectors and shared helpers
  -------------------------------------------------------------------------- */

  var ROOT_SELECTOR = '[data-pa-personalization-root]';
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var SELECTORS = {
    drawerConfig: '[data-pa-drawer-config]',
    addon: '[data-pa-addon]',
    extra: '[data-pa-extra]',
    uploadkitRoot: '[data-pa-uploadkit-root]',
    laserBlock: '[data-pa-laser-block]',
    laserType: '[data-pa-laser-type]',
    laserField: '[data-pa-laser-field]',
    laserBackBlock: '[data-pa-laser-back-block]',
    laserBackCard: '[data-pa-laser-back-card]',
    morseBlock: '[data-pa-morse-block]',
    fingerprintBlock: '[data-pa-fingerprint-block]',
    productForm: 'form[action*="/cart/add"]',
    productVariantId: '[name="id"]',
    productSubtitle: '[data-pa-product-subtitle]',
    total: '[data-pa-total]',
    submit: '[data-pa-submit]',
    submitError: '[data-pa-submit-error]',
    submitErrorText: '[data-pa-submit-error-text]',
  };

  var PROPERTY_NAMES = {
    personalization: 'Personalização',
    engravingType: '_Tipo de gravação',
    mainItem: '_Artigo principal',
    engraving: 'Gravação',
    noPersonalization: 'Sem personalização',
    engravingTop: 'Gravação superior',
    engravingBottom: 'Gravação inferior',
    engravingBack: 'Gravação traseira',
    engravingCount: 'Número de gravações',
    morseSource: 'Texto original',
    morseCode: 'Código Morse',
    designUrl: 'Design URL',
    defaultLaserText: 'Texto a gravar',
    handwrittenLetterMessage: '_Mensagem da carta',
    photoCardImage: 'Fotografia',
    photoCardDirectLink: '_Fotografia (direct link)',
  };

  var CART_ADD_ENDPOINT = '/cart/add.js';
  var UPLOADKIT_SCRIPT_URL = 'https://assets.getuploadkit.com/assets/uploadkit-app.js';
  var photoCardUploadkitScriptRequest = null;
  var photoCardUploadkitScriptRefreshAttempted = false;

  function createLaserBackLinkId() {
    return 'pa-lb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return toArray((scope || document).querySelectorAll(selector));
  }

  function scrollElementIntoView(element) {
    if (!element || typeof element.scrollIntoView !== 'function') return;

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  function setValidationState(container, errorSelector, errorTextSelector, message, options) {
    var settings = options || {};
    var error;
    var errorText;
    var hasMessage = !!message;

    if (!container) return false;

    error = errorSelector ? qs(errorSelector, container) : null;
    errorText = errorTextSelector ? qs(errorTextSelector, container) : null;

    if (settings.requireErrorElements && (!error || !errorText)) {
      return false;
    }

    container.classList.toggle('has-error', hasMessage);

    if (errorText) {
      errorText.textContent = message || '';
    }

    if (error) {
      error.hidden = !hasMessage;
    }

    if (hasMessage && settings.scroll) {
      scrollElementIntoView(container);
    }

    return true;
  }

  function getConfig(section) {
    var configScript = qs(SELECTORS.drawerConfig, section);
    var config = {};

    if (!configScript) return config;

    try {
      config = JSON.parse(configScript.textContent || '{}');
    } catch (error) {
      console.warn('PA Drawer: configuração JSON inválida.', error);
      config = {};
    }

    return config;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function initPaPersonalizationDrawer(section) {
    if (!section) return;

    if (section.dataset.paDrawerInitialized === 'true') {
      return;
    }

    /*
      Keep the fixed drawer outside product/layout wrappers. Some mobile theme
      wrappers create their own containing block, which makes a fixed panel
      render almost entirely outside the emulated viewport.
    */
    if (document.body && section.parentElement !== document.body) {
      document.body.appendChild(section);
    }

    section.dataset.paDrawerInitialized = 'true';

    var config = getConfig(section);

    var addonCards = qsa(SELECTORS.addon, section);
    var extraCards = qsa(SELECTORS.extra, section);
    var extrasBundleCard = section.querySelector('[data-pa-extras-bundle]');
    var extrasBundleActive = false;

    var microPhotoUploadkit = qs('[data-pa-uploadkit-root="micro-photo"]', section);
    var dualMicroPhotoUploadkit = qs('[data-pa-uploadkit-root="dual-micro-photo"]', section);
    var laserPhotoUploadkit = qs('[data-pa-uploadkit-root="laser-photo"]', section);
    var allUploadkitRoots = qsa(SELECTORS.uploadkitRoot, section);

    var laserBlock = qs(SELECTORS.laserBlock, section);
    var laserOptionInputs = laserBlock ? qsa(SELECTORS.laserType, laserBlock) : [];
    var laserFields = laserBlock ? qsa(SELECTORS.laserField, laserBlock) : [];
    var laserBackBlock = qs(SELECTORS.laserBackBlock, section);
    var laserBackCard = laserBackBlock ? qs(SELECTORS.laserBackCard, laserBackBlock) : null;

    var coordinateTool = laserBlock ? laserBlock.querySelector('[data-pa-coordinates-tool]') : null;
    var coordinateQueryInput = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinates-query]') : null;
    var coordinateSearchButton = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinates-search]') : null;
    var coordinateOutput = coordinateTool ? laserBlock.querySelector('[data-pa-coordinates-output]') : null;
    var coordinateResult = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinates-result]') : null;
    var coordinateLatDisplay = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinate-lat-display]') : null;
    var coordinateLngDisplay = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinate-lng-display]') : null;
    var coordinateLocationDisplay = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinate-location]') : null;
    var coordinateChangeButton = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinates-change]') : null;
    var coordinateMapEl = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinates-map]') : null;
    var coordinateStatus = coordinateTool ? coordinateTool.querySelector('[data-pa-coordinates-status]') : null;
    var coordinateMap = null;
    var coordinateMarker = null;

    var starMapTool = laserBlock ? laserBlock.querySelector('[data-pa-star-map-tool]') : null;
    var starLocationInput = starMapTool ? starMapTool.querySelector('[data-pa-star-location-query]') : null;
    var starLocationSearchButton = starMapTool ? starMapTool.querySelector('[data-pa-star-location-search]') : null;
    var starLocationDropdown = starMapTool ? starMapTool.querySelector('[data-pa-star-location-dropdown]') : null;
    var starDateInput = starMapTool ? starMapTool.querySelector('[data-pa-star-date]') : null;
    var starTimeInput = starMapTool ? starMapTool.querySelector('[data-pa-star-time]') : null;
    var starLocationDisplay = starMapTool ? starMapTool.querySelector('[data-pa-star-location-display]') : null;
    var starLocationVisual = starMapTool ? starMapTool.querySelector('[data-pa-star-location-visual]') : null;
    var starCoordinatesDisplay = starMapTool ? starMapTool.querySelector('[data-pa-star-coordinates-display]') : null;
    var starLatitudeInput = starMapTool ? starMapTool.querySelector('[data-pa-star-latitude]') : null;
    var starLongitudeInput = starMapTool ? starMapTool.querySelector('[data-pa-star-longitude]') : null;
    var starMapResult = starMapTool ? starMapTool.querySelector('[data-pa-star-map-result]') : null;
    var starMapStatus = starMapTool ? starMapTool.querySelector('[data-pa-star-map-status]') : null;
    var starMapNote = laserBlock ? laserBlock.querySelector('.pa-star-map-note') : null;
    var starLocationField = starMapTool ? starMapTool.querySelector('[data-pa-star-field="location"]') : null;
    var starDateField = starMapTool ? starMapTool.querySelector('[data-pa-star-field="date"]') : null;
    var starTimeField = starMapTool ? starMapTool.querySelector('[data-pa-star-field="time"]') : null;
    var starLocationSuggestions = [];
    var starSuggestionRequestTimer = null;
    var starSuggestionSearchController = null;
    var starSuggestionRequestId = 0;
    var starLocationSearchController = null;
    var starLocationSearchRequestId = 0;
    var coordinateSearchController = null;
    var coordinateSearchRequestId = 0;

    var dynamicTextControl = laserBlock ? laserBlock.querySelector('[data-pa-dynamic-text-control]') : null;
    var dynamicTextModeInputs = dynamicTextControl ? dynamicTextControl.querySelectorAll('[data-pa-dynamic-text-mode]') : [];
    var dynamicTextTopLine = dynamicTextControl ? dynamicTextControl.querySelector('[data-pa-dynamic-text-line="top"]') : null;
    var dynamicTextBottomLine = dynamicTextControl ? dynamicTextControl.querySelector('[data-pa-dynamic-text-line="bottom"]') : null;
    var dynamicTextTopInput = dynamicTextControl ? dynamicTextControl.querySelector('[data-pa-dynamic-text-input="top"]') : null;
    var dynamicTextBottomInput = dynamicTextControl ? dynamicTextControl.querySelector('[data-pa-dynamic-text-input="bottom"]') : null;
    var dynamicTextNote = laserBlock ? laserBlock.querySelector('[data-pa-dynamic-text-note]') : null;

    var variantPlatesControl = laserBlock ? laserBlock.querySelector('[data-pa-variant-plates-control]') : null;
    var variantPlateLines = variantPlatesControl ? variantPlatesControl.querySelectorAll('[data-pa-variant-plate-line]') : [];
    var variantPlateInputs = variantPlatesControl ? variantPlatesControl.querySelectorAll('[data-pa-variant-plate-input]') : [];
    var variantPlatesCountLabel = variantPlatesControl ? variantPlatesControl.querySelector('[data-pa-variant-plates-count]') : null;

    var morseBlock = qs(SELECTORS.morseBlock, section);
    var morseField = morseBlock ? morseBlock.querySelector('[data-pa-morse-field]') : null;
    var morseInput = morseBlock ? morseBlock.querySelector('[data-pa-morse-input]') : null;
    var morsePreview = morseBlock ? morseBlock.querySelector('[data-pa-morse-preview]') : null;
    var morseOutput = morseBlock ? morseBlock.querySelector('[data-pa-morse-output]') : null;
    var morseCounter = morseBlock ? morseBlock.querySelector('[data-pa-morse-counter]') : null;
    var morseProductImage = morseBlock ? morseBlock.querySelector('[data-pa-morse-product-image]') : null;
    var morseInputTimer = null;

    var fingerprintBlock = qs(SELECTORS.fingerprintBlock, section);
    var fingerprintCountLabel = fingerprintBlock ? fingerprintBlock.querySelector('[data-pa-fingerprint-count]') : null;
    var fingerprintInstruction = fingerprintBlock ? fingerprintBlock.querySelector('[data-pa-fingerprint-instruction]') : null;
    var fingerprintDoubleNote = fingerprintBlock ? fingerprintBlock.querySelector('[data-pa-fingerprint-double-note]') : null;
    var fingerprintCountOptions = fingerprintBlock ? fingerprintBlock.querySelectorAll('[data-pa-fingerprint-count-option]') : [];

    var engravingSelector = section.querySelector('[data-pa-engraving-selector]');
    var engravingDetail = section.querySelector('[data-pa-engraving-detail]');
    var engravingChoiceInputs = engravingSelector ? engravingSelector.querySelectorAll('[data-pa-engraving-choice]') : [];
    var engravingBackButton = section.querySelector('[data-pa-engraving-back]');
    var engravingDetailTitle = section.querySelector('[data-pa-engraving-detail-title]');
    var engravingImagesScript = section.querySelector('[data-pa-engraving-images]');
    var engravingVariantImages = {};
    var engravingPhotoIntro = section.querySelector('[data-pa-engraving-photo-intro]');
    var engravingPhotoTitle = section.querySelector('[data-pa-engraving-photo-title]');
    var engravingPhotoCopy = section.querySelector('[data-pa-engraving-photo-copy]');

    var totalEl = qs(SELECTORS.total, section);
    var submitButton = qs(SELECTORS.submit, section);
    var submitError = qs(SELECTORS.submitError, section);
    var submitErrorText = qs(SELECTORS.submitErrorText, section);
    var submitDefaultText = submitButton ? submitButton.textContent.trim() : 'Adicionar ao carrinho';

    var basePrice = Number(section.getAttribute('data-product-price')) || 0;
    var mainProductTitle = section.getAttribute('data-product-full-title') || section.getAttribute('data-product-title') || '';
    var isSubmitting = false;
    var productData = null;
    var lastOpenButton = null;
    var preferredProductForm = null;
    var lastModalOpenButton = null;
    var activeModal = null;
    var drawer = section.querySelector('.pa-personalization-drawer');
    var drawerBody = section.querySelector('.pa-personalization-body');
    var eventController = typeof AbortController === 'function' ? new AbortController() : null;
    var eventSignal = eventController ? eventController.signal : null;
    var mutationObservers = [];
    var scopedEventListeners = [];
    var scopedTimeouts = [];

    /* ------------------------------------------------------------------------
      2. Section lifecycle, scoped listeners, timers and cleanup
    ------------------------------------------------------------------------ */

    function addScopedEventListener(target, type, handler, options) {
      var listenerOptions = options ? Object.assign({}, options) : {};

      if (!target || typeof target.addEventListener !== 'function') return;

      if (eventSignal) {
        listenerOptions.signal = eventSignal;
      }

      target.addEventListener(type, handler, listenerOptions);

      scopedEventListeners.push({
        target: target,
        type: type,
        handler: handler,
        options: listenerOptions,
      });
    }

    function removeScopedEventListeners() {
      scopedEventListeners.forEach(function (listener) {
        listener.target.removeEventListener(listener.type, listener.handler, listener.options);
      });
      scopedEventListeners = [];
    }

    function setScopedTimeout(callback, delay) {
      var timerId = window.setTimeout(function () {
        var timerIndex = scopedTimeouts.indexOf(timerId);

        if (timerIndex > -1) {
          scopedTimeouts.splice(timerIndex, 1);
        }

        callback();
      }, delay);

      scopedTimeouts.push(timerId);
      return timerId;
    }

    function clearScopedTimeout(timerId) {
      var timerIndex;

      if (!timerId) return;

      window.clearTimeout(timerId);

      timerIndex = scopedTimeouts.indexOf(timerId);
      if (timerIndex > -1) {
        scopedTimeouts.splice(timerIndex, 1);
      }
    }

    function clearScopedTimeouts() {
      scopedTimeouts.forEach(function (timerId) {
        window.clearTimeout(timerId);
      });
      scopedTimeouts = [];
    }

    function registerMutationObserver(observer) {
      if (observer && typeof observer.disconnect === 'function') {
        mutationObservers.push(observer);
      }
    }

    function abortSearchController(controller) {
      if (controller && typeof controller.abort === 'function') {
        controller.abort();
      }
    }

    function isAbortError(error) {
      return !!(error && error.name === 'AbortError');
    }

    function isVisibleElement(element) {
      return !!(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length));
    }

    function setElementInert(element, isInert) {
      if (!element) return;

      if (isInert) {
        element.setAttribute('inert', '');
      } else {
        element.removeAttribute('inert');
      }

      if ('inert' in element) {
        element.inert = !!isInert;
      }
    }

    function getFocusableElements(container) {
      if (!container) return [];

      return qsa(FOCUSABLE_SELECTOR, container).filter(function (element) {
        return !element.disabled
          && element.getAttribute('aria-hidden') !== 'true'
          && !element.closest('[inert]')
          && isVisibleElement(element);
      });
    }

    function focusFirstInteractiveElement(container, preferredElement) {
      var focusTarget = preferredElement && isVisibleElement(preferredElement)
        ? preferredElement
        : getFocusableElements(container)[0];

      if (focusTarget && typeof focusTarget.focus === 'function') {
        setScopedTimeout(function () {
          focusTarget.focus({ preventScroll: true });
        }, 80);
      }
    }

    function trapFocus(event, container) {
      var focusableElements;
      var firstElement;
      var lastElement;

      if (!container || event.key !== 'Tab') return;

      focusableElements = getFocusableElements(container);

      if (!focusableElements.length) {
        event.preventDefault();

        if (typeof container.focus === 'function') {
          container.focus({ preventScroll: true });
        }

        return;
      }

      firstElement = focusableElements[0];
      lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus({ preventScroll: true });
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus({ preventScroll: true });
      }
    }

    function isDrawerOpen() {
      return !!(drawer && drawer.getAttribute('aria-hidden') === 'false');
    }

    function syncOpenButtonsState(isOpen) {
      qsa('[data-pa-personalization-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    function destroyDrawerInstance() {
      mutationObservers.forEach(function (observer) {
        observer.disconnect();
      });
      mutationObservers = [];

      if (starSuggestionRequestTimer) {
        clearScopedTimeout(starSuggestionRequestTimer);
        starSuggestionRequestTimer = null;
      }

      if (morseInputTimer) {
        clearScopedTimeout(morseInputTimer);
        morseInputTimer = null;
      }

      abortSearchController(starSuggestionSearchController);
      starSuggestionSearchController = null;
      starSuggestionRequestId += 1;
      abortSearchController(starLocationSearchController);
      starLocationSearchController = null;
      starLocationSearchRequestId += 1;
      abortSearchController(coordinateSearchController);
      coordinateSearchController = null;
      coordinateSearchRequestId += 1;

      clearScopedTimeouts();

      if (eventController) {
        eventController.abort();
      }

      removeScopedEventListeners();

      section.dataset.paDrawerInitialized = 'false';
      delete section.paDrawerDestroy;
    }

    section.paDrawerDestroy = destroyDrawerInstance;

    try {
      productData = JSON.parse(section.getAttribute('data-product-json') || '{}');
    } catch (error) {
      productData = null;
    }

    if (engravingImagesScript) {
      try {
        engravingVariantImages = JSON.parse(engravingImagesScript.textContent || '{}');
      } catch (error) {
        console.warn('PA Drawer: imagens de Gravura inválidas.', error);
        engravingVariantImages = {};
      }
    }

    var uploadButtonDefaultText = config.uploadButtonDefaultText || 'Carregue a sua foto';
    var moneyFormat = config.moneyFormat || '';

    /* ------------------------------------------------------------------------
      3. Product form and variant synchronization
    ------------------------------------------------------------------------ */

    function findVariantById(variantId) {
      if (!productData || !Array.isArray(productData.variants)) return null;

      return productData.variants.find(function (variant) {
        return String(variant.id) === String(variantId);
      }) || null;
    }

    function isUsableProductForm(form) {
      return !!(
        form &&
        typeof form.matches === 'function' &&
        form.matches(SELECTORS.productForm) &&
        !section.contains(form) &&
        qs(SELECTORS.productVariantId, form)
      );
    }

    function getMainProductForms() {
      return qsa(SELECTORS.productForm, document).filter(function (form) {
        return isUsableProductForm(form);
      });
    }

    function getVariantIdFromForm(form) {
      if (!form) return null;

      var checkedRadio = form.querySelector('input[name="id"][type="radio"]:checked');
      if (checkedRadio && checkedRadio.value) return checkedRadio.value;

      var select = form.querySelector('select[name="id"]');
      if (select && select.value) return select.value;

      var input = form.querySelector('input[name="id"]');
      if (input && input.value) return input.value;

      return null;
    }

    function getValidVariantIdFromForm(form) {
      var variantId = getVariantIdFromForm(form);

      if (!variantId || !findVariantById(variantId)) return null;

      return variantId;
    }

    function getPreferredProductFormFromOpenButton(openButton) {
      var formSelector;
      var selectedForm;
      var closestForm;

      if (!openButton) return null;

      formSelector = openButton.getAttribute('data-pa-product-form');

      if (formSelector) {
        try {
          selectedForm = document.querySelector(formSelector);
        } catch (error) {
          console.warn('PA Drawer: seletor data-pa-product-form inválido.', formSelector);
        }

        if (isUsableProductForm(selectedForm) && getValidVariantIdFromForm(selectedForm)) {
          return selectedForm;
        }
      }

      closestForm = openButton.closest(SELECTORS.productForm);

      if (isUsableProductForm(closestForm) && getValidVariantIdFromForm(closestForm)) {
        return closestForm;
      }

      return null;
    }

    function getActiveVariantIdFromPage(preferredForm) {
      var preferredVariantId = getValidVariantIdFromForm(preferredForm);
      if (preferredVariantId) return preferredVariantId;

      var productForms = getMainProductForms();
      var visibleForm = productForms.find(function (form) {
        return form.offsetParent !== null && getValidVariantIdFromForm(form);
      });

      var visibleVariantId = getValidVariantIdFromForm(visibleForm);
      if (visibleVariantId) return visibleVariantId;

      var firstVariantId = getValidVariantIdFromForm(productForms[0]);
      if (firstVariantId) return firstVariantId;

      return section.getAttribute('data-product-variant-id');
    }

    function updateDrawerProductSubtitle(productTitle, variantTitle) {
      var subtitle = qs(SELECTORS.productSubtitle, section);
      if (!subtitle) return;

      subtitle.innerHTML = '<strong>' + escapeHtml(productTitle) + '</strong>';

      if (variantTitle && variantTitle !== 'Default Title') {
        subtitle.innerHTML += ' - ' + escapeHtml(variantTitle);
      }
    }

    function updateEngravingImages(variantId) {
      if (!engravingSelector) return;

      var images = engravingVariantImages[String(variantId)] || {};

      qsa('[data-pa-engraving-image]', engravingSelector).forEach(function (media) {
        var optionKey = media.getAttribute('data-engraving-key');
        var fallbackUrl = media.getAttribute('data-fallback-image') || '';
        var imageUrl = images[optionKey] || fallbackUrl;
        var image = media.querySelector('img');
        var placeholder = media.querySelector('.pa-engraving-option-placeholder');

        if (!imageUrl) return;

        if (!image) {
          image = document.createElement('img');
          image.loading = 'lazy';
          image.alt = optionKey || 'Exemplo de personalização';
          media.appendChild(image);
        }

        image.src = imageUrl;
        image.removeAttribute('srcset');
        if (placeholder) placeholder.remove();
      });
    }

    function syncMainProductFromActiveVariant(preferredForm) {
      var activeVariantId = getActiveVariantIdFromPage(preferredForm);
      var activeVariant = findVariantById(activeVariantId);

      if (!activeVariant) return;

      var productTitle = section.getAttribute('data-product-title') || '';
      var variantTitle = activeVariant.title || '';
      var fullTitle = productTitle;

      if (variantTitle && variantTitle !== 'Default Title') {
        fullTitle += ' - ' + variantTitle;
      }

      section.setAttribute('data-product-variant-id', activeVariant.id);
      section.setAttribute('data-product-price', activeVariant.price);
      section.setAttribute('data-product-full-title', fullTitle);

      basePrice = Number(activeVariant.price) || 0;
      mainProductTitle = fullTitle;

      updateDrawerProductSubtitle(productTitle, variantTitle);
      updateVariantPlateFields();
      updateFingerprintState();
      updateMorseVariantImage(activeVariant);
      updateEngravingImages(activeVariant.id);
    }

    function scheduleMainProductSync(preferredForm) {
      syncMainProductFromActiveVariant(preferredForm);

      [50, 150, 350].forEach(function (delay) {
        setScopedTimeout(function () {
          syncMainProductFromActiveVariant(preferredForm);
          updateVariantPlateFields();
          updateFingerprintState();
          updateTotal();
        }, delay);
      });
    }

    /* ------------------------------------------------------------------------
      4. Shared validation and UploadKit property helpers
    ------------------------------------------------------------------------ */

    function showInlineFieldError(field, message) {
      return setValidationState(
        field,
        '[data-pa-laser-field-error]',
        '[data-pa-laser-field-error-text]',
        message,
        { scroll: true, requireErrorElements: true }
      );
    }

    function clearInlineLaserErrors() {
      if (!laserBlock && !laserBackBlock) return;

      qsa('[data-pa-laser-field-error], [data-pa-laser-options-error]', section).forEach(function (error) {
        error.hidden = true;
      });

      qsa('[data-pa-laser-field-error-text], [data-pa-laser-options-error-text]', section).forEach(function (errorText) {
        errorText.textContent = '';
      });
    }

    function showInlineLaserOptionsError(optionsWrapper, message) {
      return setValidationState(
        optionsWrapper,
        '[data-pa-laser-options-error]',
        '[data-pa-laser-options-error-text]',
        message,
        { scroll: true, requireErrorElements: true }
      );
    }

    function showLaserError(message, targetField) {
      if (!laserBlock && !laserBackBlock) return;

      var laserError = laserBlock ? laserBlock.querySelector('[data-pa-laser-error]') : null;
      var laserErrorText = laserBlock ? laserBlock.querySelector('[data-pa-laser-error-text]') : null;
      var visibleField = targetField || getVisibleLaserField();
      var laserOptionsWrapper =
        laserBlock ? (
          laserBlock.querySelector('[data-pa-laser-options]') ||
          laserBlock.querySelector('.pa-laser-options')
        ) : null;
      var scrollTarget = visibleField || laserBlock || laserBackBlock;

      /*
        Clear previous field error states before highlighting the field
        that actually needs attention. This includes the main laser block,
        standalone back engraving block and option selector.
      */
      qsa('[data-pa-laser-field], [data-pa-laser-back-field], [data-pa-laser-options], .pa-laser-options', section).forEach(function (field) {
        field.classList.remove('has-error');
      });

      if (!targetField && laserOptionsWrapper && !laserBlock.querySelector('[data-pa-laser-type]:checked')) {
        if (showInlineLaserOptionsError(laserOptionsWrapper, message)) {
          if (laserError) {
            laserError.hidden = true;
          }

          return;
        }

        laserOptionsWrapper.classList.add('has-error');
      }

      if (visibleField) {
        visibleField.classList.add('has-error');
      }

      if (visibleField && showInlineFieldError(visibleField, message)) {
        if (laserError) {
          laserError.hidden = true;
        }

        return;
      }

      if (laserErrorText) {
        laserErrorText.textContent = message;
      }

      if (laserError) {
        laserError.hidden = false;
      }

      scrollElementIntoView(scrollTarget);
    }

    function clearLaserError() {
      if (!laserBlock && !laserBackBlock) return;
      clearInlineLaserErrors();
      clearStarMapErrors();
      clearDynamicTextErrors();
      clearVariantPlateErrors();

      var laserError = laserBlock ? laserBlock.querySelector('[data-pa-laser-error]') : null;
      if (laserError) {
        laserError.hidden = true;
      }

      /*
        Remove visual error states from all laser inputs, including the
        optional standalone back engraving textarea.
      */
      qsa('[data-pa-laser-field], [data-pa-laser-back-field], [data-pa-laser-options], .pa-laser-options', section).forEach(function (field) {
        field.classList.remove('has-error');
      });

      clearLaserPhotoErrorIfPhotoExists();
    }

    /* UploadKit: read only the original app-generated line item properties. */
    function getUploadKitProperties(uploadkitRoot) {
      if (!uploadkitRoot) return [];

      return toArray(uploadkitRoot.querySelectorAll('input[name^="properties["]'))
        .map(function (input) {
          var propertyName = input.name.replace('properties[', '').replace(']', '');
          var propertyValue = input.value || '';

          return {
            name: propertyName,
            value: propertyValue,
          };
        })
        .filter(function (property) {
          return !!property.name && !!property.value;
        });
    }

    function hasUploadKitProperty(uploadkitRoot) {
      return getUploadKitProperties(uploadkitRoot).length > 0;
    }

    function getDualMicroPhotoProperties() {
      if (!dualMicroPhotoUploadkit) return [];

      /*
        Keep dual-micro-photo aligned with the existing micro-photo behaviour:
        copy the original UploadKit properties exactly as the app generates them.
        This lets the cart, checkout and order render the properties using the
        implementation that already exists in the theme/site.
      */
      return getUploadKitProperties(dualMicroPhotoUploadkit);
    }

    function getUploadKitPropertyPreviewUrl(value) {
      var stringValue = String(value || '');
      var match = stringValue.match(/https?:\/\/[^\s"'<>]+/i);

      if (!match) return '';

      return match[0].replace(/[),.;]+$/, '');
    }

    function getBackgroundImageUrl(element) {
      var backgroundImage;
      var match;

      if (!element) return '';

      backgroundImage = element.style && element.style.backgroundImage;

      if (!backgroundImage && window.getComputedStyle) {
        backgroundImage = window.getComputedStyle(element).backgroundImage;
      }

      match = String(backgroundImage || '').match(/url\(["']?(.+?)["']?\)/i);

      return match ? match[1] : '';
    }

    function getUploadKitThumbnailPreviewUrls(uploadkitRoot) {
      if (!uploadkitRoot) return [];

      return toArray(uploadkitRoot.querySelectorAll('.uploadkit-thumbnail')).map(function (thumbnail) {
        var image = thumbnail.matches && thumbnail.matches('img') ? thumbnail : thumbnail.querySelector('img');
        var backgroundElement = thumbnail.querySelector('[style*="background"]') || thumbnail;

        if (image && (image.currentSrc || image.src)) {
          return image.currentSrc || image.src;
        }

        return getBackgroundImageUrl(backgroundElement);
      }).filter(Boolean);
    }

    function findUploadKitPropertyForPreview(properties, previewUrl, fallbackIndex) {
      var directMatch = properties.filter(function (property) {
        return previewUrl && property.value.indexOf(previewUrl) !== -1;
      })[0];
      var propertiesWithPreview = properties.filter(function (property) {
        return !!getUploadKitPropertyPreviewUrl(property.value);
      });

      return directMatch || propertiesWithPreview[fallbackIndex] || properties[fallbackIndex] || null;
    }

    function getPhotoCardSelectableUploadKitProperties(uploadkitRoot, limit) {
      var properties = getUploadKitProperties(uploadkitRoot);
      var thumbnailPreviewUrls = getUploadKitThumbnailPreviewUrls(uploadkitRoot).slice(0, limit);
      var propertiesWithPreview;

      if (thumbnailPreviewUrls.length) {
        return thumbnailPreviewUrls.map(function (previewUrl, index) {
          var property = findUploadKitPropertyForPreview(properties, previewUrl, index);

          return {
            name: property ? property.name : '',
            value: property ? property.value : previewUrl,
            previewUrl: previewUrl,
            directLinkValue: property ? getMatchingUploadKitDirectLinkValue(properties, property.name) : '',
          };
        }).filter(function (property) {
          return !!property.value;
        });
      }

      propertiesWithPreview = properties.filter(function (property) {
        return !!getUploadKitPropertyPreviewUrl(property.value);
      });

      return (propertiesWithPreview.length ? propertiesWithPreview : properties).slice(0, limit).map(function (property) {
        return {
          name: property.name,
          value: property.value,
          previewUrl: getUploadKitPropertyPreviewUrl(property.value),
          directLinkValue: getMatchingUploadKitDirectLinkValue(properties, property.name),
        };
      });
    }

    function getMatchingUploadKitDirectLinkValue(properties, propertyName) {
      var directLinkName;
      var directLinkProperty;

      if (!propertyName || propertyName.charAt(0) === '_') return '';

      directLinkName = '_' + propertyName + ' (direct link)';
      directLinkProperty = properties.filter(function (property) {
        return property.name === directLinkName;
      })[0];

      return directLinkProperty ? directLinkProperty.value : '';
    }

    function getMainPhotoUploadKitProperties() {
      if (dualMicroPhotoUploadkit) return getPhotoCardSelectableUploadKitProperties(dualMicroPhotoUploadkit, 2);
      if (microPhotoUploadkit) return getPhotoCardSelectableUploadKitProperties(microPhotoUploadkit, 1);
      if (laserPhotoUploadkit) return getPhotoCardSelectableUploadKitProperties(laserPhotoUploadkit, 1);
      return [];
    }

    function getAddonPhotoCardPanel(card) {
      var item = card ? card.closest('[data-pa-addon-item]') : null;
      return item ? item.querySelector('[data-pa-addon-photo-card-panel]') : null;
    }

    function getAddonPhotoCardUploadkit(panel) {
      return panel ? panel.querySelector('[data-pa-uploadkit-root="photo-card"]') : null;
    }

    function getAddonPhotoCardSelectedInput(panel) {
      return panel ? panel.querySelector('[data-pa-photo-card-source]:checked') : null;
    }

    function getAddonPhotoCardSelectedProperty(panel) {
      var selectedProperties = getAddonPhotoCardSelectedProperties(panel);

      return selectedProperties[0] || null;
    }

    function getAddonPhotoCardSelectedProperties(panel) {
      var selectedInput = getAddonPhotoCardSelectedInput(panel);
      var source;
      var uploadkitRoot;
      var uploadkitProperties;

      if (!selectedInput) return [];

      source = selectedInput.getAttribute('data-photo-card-source');

      if (source === 'new') {
        uploadkitRoot = getAddonPhotoCardUploadkit(panel);
        uploadkitProperties = getUploadKitProperties(uploadkitRoot);

        var visiblePhotoProperty = uploadkitProperties.filter(function (property) {
          return property.name.charAt(0) !== '_';
        })[0];
        var directPhotoProperty = uploadkitProperties.filter(function (property) {
          return property.name.charAt(0) === '_' && property.name.indexOf('(direct link)') !== -1;
        })[0];

        return [
          {
            name: PROPERTY_NAMES.photoCardImage,
            value: visiblePhotoProperty ? visiblePhotoProperty.value : '',
            source: 'Fotografia nova',
          },
          {
            name: PROPERTY_NAMES.photoCardDirectLink,
            value: directPhotoProperty ? directPhotoProperty.value : '',
            source: 'Fotografia nova',
          },
        ].filter(function (property) {
          return !!property.value;
        });
      }

      return [
        {
          name: PROPERTY_NAMES.photoCardImage,
          value: selectedInput.getAttribute('data-photo-card-property-value') || '',
          source: selectedInput.getAttribute('data-photo-card-label') || '',
        },
        {
          name: PROPERTY_NAMES.photoCardDirectLink,
          value: selectedInput.getAttribute('data-photo-card-direct-link-value') || '',
          source: selectedInput.getAttribute('data-photo-card-label') || '',
        },
      ].filter(function (property) {
        return !!property.value;
      });
    }

    function setAddonPhotoCardError(card, message, shouldScroll) {
      var panel = getAddonPhotoCardPanel(card);

      if (!panel) return false;

      return setValidationState(
        panel,
        '[data-pa-photo-card-error]',
        '[data-pa-photo-card-error-text]',
        message,
        { scroll: !!shouldScroll, requireErrorElements: true }
      );
    }

    function syncAddonPhotoCardUploadVisibility(panel) {
      var selectedInput = getAddonPhotoCardSelectedInput(panel);
      var uploadWrap = panel ? panel.querySelector('[data-pa-photo-card-upload]') : null;
      var shouldShowUpload = !!selectedInput && selectedInput.getAttribute('data-photo-card-source') === 'new';

      if (uploadWrap) {
        uploadWrap.classList.toggle('is-active', shouldShowUpload);
      }
    }

    function syncAddonPhotoCardNewOptionPreview(panel) {
      var uploadkitRoot = getAddonPhotoCardUploadkit(panel);
      var previewUrl = getUploadKitThumbnailPreviewUrls(uploadkitRoot)[0] || '';
      var newOption = panel ? panel.querySelector('[data-pa-photo-card-new-option]') : null;
      var thumb = panel ? panel.querySelector('[data-pa-photo-card-new-thumb]') : null;
      var title = panel ? panel.querySelector('[data-pa-photo-card-new-title]') : null;
      var detail = panel ? panel.querySelector('[data-pa-photo-card-new-detail]') : null;
      var uploadTrigger = panel ? panel.querySelector('[data-pa-photo-card-upload-trigger]') : null;

      if (!newOption || !thumb) return;

      newOption.classList.toggle('has-uploaded-photo', !!previewUrl);
      thumb.classList.toggle('pa-addon-photo-card-thumb--empty', !previewUrl);
      thumb.style.backgroundImage = previewUrl ? 'url("' + previewUrl.replace(/"/g, '%22') + '")' : '';

      if (title) {
        title.textContent = previewUrl ? 'Fotografia nova' : 'Adicionar nova fotografia';
      }

      if (detail) {
        detail.textContent = previewUrl
          ? 'Usar esta fotografia apenas para o cartão.'
          : 'Usar uma imagem diferente apenas para o cartão.';
      }

      if (uploadTrigger) {
        uploadTrigger.textContent = previewUrl ? 'Substituir' : 'Adicionar';
      }
    }

    function requestPhotoCardUploadkitScriptRefresh() {
      var script;

      if (photoCardUploadkitScriptRequest || photoCardUploadkitScriptRefreshAttempted) return;

      photoCardUploadkitScriptRefreshAttempted = true;
      script = document.createElement('script');
      script.src = UPLOADKIT_SCRIPT_URL;
      script.async = true;
      script.setAttribute('data-pa-photo-card-uploadkit-refresh', 'true');
      script.onload = script.onerror = function () {
        photoCardUploadkitScriptRequest = null;
      };

      photoCardUploadkitScriptRequest = script;
      document.head.appendChild(script);
    }

    function ensureAddonPhotoCardUploadkit(panel) {
      var uploadkitRoot = getAddonPhotoCardUploadkit(panel);

      if (!uploadkitRoot) return;
      if (uploadkitRoot.querySelector('.uploadkit-button, .uploadkit-upload-field, input[name^="properties["]')) return;
      if (uploadkitRoot.getAttribute('data-pa-uploadkit-refreshing') === 'true') return;

      uploadkitRoot.setAttribute('data-pa-uploadkit-refreshing', 'true');
      requestPhotoCardUploadkitScriptRefresh();

      setScopedTimeout(function () {
        uploadkitRoot.removeAttribute('data-pa-uploadkit-refreshing');
      }, 900);
    }

    function ensureLaserPhotoUploadkit() {
      if (!laserPhotoUploadkit) return;
      if (laserPhotoUploadkit.querySelector('.uploadkit-button, .uploadkit-upload-field, input[name^="properties["]')) return;
      if (laserPhotoUploadkit.getAttribute('data-pa-uploadkit-refreshing') === 'true') return;

      laserPhotoUploadkit.setAttribute('data-pa-uploadkit-refreshing', 'true');
      requestPhotoCardUploadkitScriptRefresh();

      setScopedTimeout(function () {
        laserPhotoUploadkit.removeAttribute('data-pa-uploadkit-refreshing');
      }, 1200);
    }

    function getAddonPhotoCardUploadControl(panel) {
      var uploadkitRoot = getAddonPhotoCardUploadkit(panel);

      return uploadkitRoot
        ? uploadkitRoot.querySelector('.uploadkit-button, .uploadkit-upload-field button, button, input[type="file"]')
        : null;
    }

    function openAddonPhotoCardUploadkit(panel) {
      var uploadButton = getAddonPhotoCardUploadControl(panel);

      if (!uploadButton || typeof uploadButton.click !== 'function') return false;

      uploadButton.click();
      return true;
    }

    function openAddonPhotoCardUploadkitWhenReady(panel, card) {
      if (openAddonPhotoCardUploadkit(panel)) return;

      ensureAddonPhotoCardUploadkit(panel);

      setScopedTimeout(function () {
        if (openAddonPhotoCardUploadkit(panel)) return;
      }, 350);

      setScopedTimeout(function () {
        if (openAddonPhotoCardUploadkit(panel)) return;

        if (card) {
          setAddonPhotoCardError(
            card,
            'Não foi possível abrir o carregamento da fotografia do cartão. Confirme a configuração do UploadKit neste extra.',
            false
          );
        }
      }, 1100);
    }

    function selectAddonPhotoCardNewUpload(panel) {
      var input = panel ? panel.querySelector('[data-pa-photo-card-source][data-photo-card-source="new"]') : null;

      if (!input) return false;

      input.checked = true;
      syncAddonPhotoCardUploadVisibility(panel);
      syncAddonPhotoCardNewOptionPreview(panel);
      updateAddonPhotoCardOptionStates(panel);
      ensureAddonPhotoCardUploadkit(panel);

      return true;
    }

    function updateAddonPhotoCardOptionStates(panel) {
      qsa('.pa-addon-photo-card-option', panel).forEach(function (option) {
        var input = option.querySelector('[data-pa-photo-card-source]');
        option.classList.toggle('is-selected', !!(input && input.checked));
        if (option.hasAttribute('role')) {
          option.setAttribute('aria-checked', input && input.checked ? 'true' : 'false');
        }
      });
    }

    function renderAddonPhotoCardOptions(panel, preferExistingPhoto) {
      var optionsWrap = panel ? panel.querySelector('[data-pa-photo-card-existing-options]') : null;
      var emptyMessage = panel ? panel.querySelector('[data-pa-photo-card-empty]') : null;
      var selectedInput = getAddonPhotoCardSelectedInput(panel);
      var selectedValue = selectedInput ? selectedInput.value : '';
      var radioName = selectedInput ? selectedInput.name : '';
      var properties = getMainPhotoUploadKitProperties();

      if (!panel || !optionsWrap) return;

      if (!radioName) {
        selectedInput = panel.querySelector('[data-pa-photo-card-source]');
        radioName = selectedInput ? selectedInput.name : '';
      }

      optionsWrap.innerHTML = '';

      properties.forEach(function (property, index) {
        var value = 'main-' + index;
        var label = properties.length === 1 ? 'Fotografia' : 'Fotografia ' + (index + 1);
        var previewUrl = property.previewUrl || getUploadKitPropertyPreviewUrl(property.value);
        var option = document.createElement('label');
        var input = document.createElement('input');
        var thumb = document.createElement('span');
        var copy = document.createElement('span');
        var title = document.createElement('strong');
        var detail = document.createElement('small');

        option.className = 'pa-addon-photo-card-option';

        input.type = 'radio';
        input.name = radioName;
        input.value = value;
        input.setAttribute('data-pa-photo-card-source', '');
        input.setAttribute('data-photo-card-source', 'main');
        input.setAttribute('data-photo-card-property-name', property.name);
        input.setAttribute('data-photo-card-property-value', property.value);
        input.setAttribute('data-photo-card-direct-link-value', property.directLinkValue || '');
        input.setAttribute('data-photo-card-label', label);
        input.checked = preferExistingPhoto ? index === 0 : (selectedValue ? selectedValue === value : index === 0);

        thumb.className = 'pa-addon-photo-card-thumb';
        if (previewUrl) {
          thumb.style.backgroundImage = 'url("' + previewUrl.replace(/"/g, '%22') + '")';
        } else {
          thumb.classList.add('pa-addon-photo-card-thumb--empty');
        }

        copy.className = 'pa-addon-photo-card-option-copy';
        title.textContent = label;
        detail.textContent = previewUrl ? 'Usar a mesma fotografia da peça.' : 'Usar o ficheiro já carregado no artigo.';

        copy.appendChild(title);
        copy.appendChild(detail);
        option.appendChild(input);
        option.appendChild(thumb);
        option.appendChild(copy);
        optionsWrap.appendChild(option);
      });

      if (emptyMessage) {
        emptyMessage.hidden = properties.length > 0;
      }

      if (selectedValue && !preferExistingPhoto && !panel.querySelector('[data-pa-photo-card-source]:checked')) {
        selectedInput = panel.querySelector('[data-pa-photo-card-source][value="new"]');
        if (selectedInput) selectedInput.checked = true;
      }

      syncAddonPhotoCardUploadVisibility(panel);
      syncAddonPhotoCardNewOptionPreview(panel);
      updateAddonPhotoCardOptionStates(panel);
    }

    /* ------------------------------------------------------------------------
      5. Coordinates, star-map search and TeeInBlue integration
    ------------------------------------------------------------------------ */

    function setCoordinateStatus(message, isError) {
      if (!coordinateStatus) return;

      coordinateStatus.textContent = message || '';
      coordinateStatus.hidden = !message;
      coordinateStatus.classList.toggle('is-error', !!isError);
    }

    function formatDirectionalCoordinate(value, isLatitude) {
      var number = Number(value);
      var direction;

      if (!isFinite(number)) return '';

      if (isLatitude) {
        direction = number >= 0 ? 'N' : 'S';
      } else {
        direction = number >= 0 ? 'E' : 'W';
      }

      return Math.abs(number).toFixed(4) + '° ' + direction;
    }

    function ensureCoordinateMap(latitude, longitude) {
      if (!coordinateMapEl || !window.L) return;

      coordinateMapEl.hidden = false;

      if (!coordinateMap) {
        coordinateMap = window.L.map(coordinateMapEl, {
          scrollWheelZoom: false,
        }).setView([latitude, longitude], 13);

        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(coordinateMap);

        coordinateMarker = window.L.marker([latitude, longitude], {
          draggable: true,
        }).addTo(coordinateMap);

        coordinateMarker.on('dragend', function () {
          var position = coordinateMarker.getLatLng();
          setCoordinateOutput(position.lat, position.lng, true);
        });

        coordinateMap.on('click', function (event) {
          setCoordinateOutput(event.latlng.lat, event.latlng.lng, true);
        });
      } else {
        coordinateMap.setView([latitude, longitude], 13);
        coordinateMarker.setLatLng([latitude, longitude]);
      }

      setScopedTimeout(function () {
        if (coordinateMap) {
          coordinateMap.invalidateSize();
        }
      }, 150);
    }

    function setCoordinateOutput(latitude, longitude, manualAdjustment, locationLabel) {
      var formattedLatitude = formatDirectionalCoordinate(latitude, true);
      var formattedLongitude = formatDirectionalCoordinate(longitude, false);
      var finalCoordinates = formattedLatitude + ', ' + formattedLongitude;

      if (coordinateLatDisplay) coordinateLatDisplay.value = formattedLatitude;
      if (coordinateLngDisplay) coordinateLngDisplay.value = formattedLongitude;
      if (coordinateLocationDisplay && locationLabel) coordinateLocationDisplay.textContent = locationLabel;
      if (coordinateOutput) coordinateOutput.value = finalCoordinates;
      if (coordinateResult) coordinateResult.hidden = false;

      ensureCoordinateMap(Number(latitude), Number(longitude));

      if (manualAdjustment) {
        setCoordinateStatus('Coordenadas atualizadas.', false);
      } else {
        setCoordinateStatus('', false);
      }

      clearLaserError();
    }

    function resetCoordinateFinder() {
      if (!coordinateTool) return;

      abortSearchController(coordinateSearchController);
      coordinateSearchController = null;
      coordinateSearchRequestId += 1;

      if (coordinateQueryInput) coordinateQueryInput.value = '';
      if (coordinateLatDisplay) coordinateLatDisplay.value = '';
      if (coordinateLngDisplay) coordinateLngDisplay.value = '';
      if (coordinateLocationDisplay) coordinateLocationDisplay.textContent = '';
      if (coordinateOutput) coordinateOutput.value = '';
      if (coordinateResult) coordinateResult.hidden = true;
      if (coordinateMapEl) coordinateMapEl.hidden = true;

      setCoordinateStatus('', false);
    }

    function getStarLocationLabel(result) {
      if (!result) return '';

      return result.display_name || result.name || '';
    }

    function hideStarLocationDropdown() {
      if (!starLocationDropdown) return;

      starLocationDropdown.hidden = true;
      starLocationDropdown.innerHTML = '';
    }

    function renderStarLocationDropdown(results) {
      if (!starLocationDropdown) return;

      starLocationSuggestions = Array.isArray(results) ? results : [];
      starLocationDropdown.innerHTML = '';

      if (!starLocationSuggestions.length) {
        hideStarLocationDropdown();
        return;
      }

      starLocationSuggestions.forEach(function (result, index) {
        var label = getStarLocationLabel(result);
        if (!label) return;

        var optionButton = document.createElement('button');
        optionButton.type = 'button';
        optionButton.className = 'pa-star-location-option';
        optionButton.setAttribute('data-pa-star-location-option', String(index));

        var mainText = document.createElement('span');
        mainText.className = 'pa-star-location-option-main';
        mainText.textContent = label.split(',').slice(0, 2).join(',');

        var secondaryText = document.createElement('span');
        secondaryText.className = 'pa-star-location-option-secondary';
        secondaryText.textContent = label;

        optionButton.appendChild(mainText);
        optionButton.appendChild(secondaryText);
        starLocationDropdown.appendChild(optionButton);
      });

      starLocationDropdown.hidden = !starLocationDropdown.children.length;
    }

    function selectStarLocationResult(result) {
      var label = getStarLocationLabel(result);

      if (!result || !result.lat || !result.lon) return;

      if (starLocationInput) {
        starLocationInput.value = label;
      }

      setStarMapLocation(result.lat, result.lon, label);
      hideStarLocationDropdown();
    }

    function fetchStarLocationSuggestions(query) {
      var requestId;
      var fetchOptions = {
        headers: {
          Accept: 'application/json',
        },
      };

      abortSearchController(starSuggestionSearchController);
      starSuggestionSearchController = null;
      starSuggestionRequestId += 1;
      requestId = starSuggestionRequestId;

      if (!query || query.length < 3) {
        renderStarLocationDropdown([]);
        return;
      }

      if (typeof AbortController === 'function') {
        starSuggestionSearchController = new AbortController();
        fetchOptions.signal = starSuggestionSearchController.signal;
      }

      fetch('https://nominatim.openstreetmap.org/search?format=json&limit=5&q=' + encodeURIComponent(query), fetchOptions)
        .then(function (response) {
          if (!response.ok) throw new Error('Erro ao procurar localização.');
          return response.json();
        })
        .then(function (results) {
          if (requestId !== starSuggestionRequestId) return;

          renderStarLocationDropdown(results);
        })
        .catch(function (error) {
          if (isAbortError(error) || requestId !== starSuggestionRequestId) return;

          renderStarLocationDropdown([]);
        })
        .finally(function () {
          if (requestId === starSuggestionRequestId) {
            starSuggestionSearchController = null;
          }
        });
    }

    function scheduleStarLocationSuggestions() {
      if (!starLocationInput) return;

      clearScopedTimeout(starSuggestionRequestTimer);

      starSuggestionRequestTimer = setScopedTimeout(function () {
        starSuggestionRequestTimer = null;
        fetchStarLocationSuggestions(starLocationInput.value.trim());
      }, 280);
    }

    function getStarMapValidationField(fieldName) {
      if (fieldName === 'location') return starLocationField;
      if (fieldName === 'date') return starDateField;
      if (fieldName === 'time') return starTimeField;
      return null;
    }

    function setStarMapFieldError(fieldName, message) {
      var field = getStarMapValidationField(fieldName);

      if (!field) {
        setStarMapStatus(message, true);
        return false;
      }

      return setValidationState(
        field,
        '[data-pa-star-field-error]',
        '[data-pa-star-field-error-text]',
        message,
        { scroll: true }
      );
    }

    function clearStarMapFieldError(fieldName) {
      setValidationState(
        getStarMapValidationField(fieldName),
        '[data-pa-star-field-error]',
        '[data-pa-star-field-error-text]',
        '',
        { scroll: false }
      );
    }

    function clearStarMapErrors() {
      clearStarMapFieldError('location');
      clearStarMapFieldError('date');
      clearStarMapFieldError('time');
    }

    function getDynamicTextMode() {
      var selectedModeInput = dynamicTextControl ? dynamicTextControl.querySelector('[data-pa-dynamic-text-mode]:checked') : null;
      return selectedModeInput ? selectedModeInput.value : '';
    }

    function getDynamicTextLine(position) {
      if (position === 'top') return dynamicTextTopLine;
      if (position === 'bottom') return dynamicTextBottomLine;
      return null;
    }

    function setDynamicTextFieldError(position, message) {
      return setValidationState(
        getDynamicTextLine(position),
        '[data-pa-dynamic-text-error]',
        '[data-pa-dynamic-text-error-text]',
        message,
        { scroll: true }
      );
    }

    function clearDynamicTextFieldError(position) {
      setValidationState(
        getDynamicTextLine(position),
        '[data-pa-dynamic-text-error]',
        '[data-pa-dynamic-text-error-text]',
        '',
        { scroll: false }
      );
    }

    function clearDynamicTextErrors() {
      clearDynamicTextFieldError('top');
      clearDynamicTextFieldError('bottom');
    }

    function updateDynamicTextFields() {
      var mode = getDynamicTextMode();

      if (!dynamicTextControl) return;

      toArray(dynamicTextModeInputs).forEach(function (input) {
        var option = input.closest('[data-pa-dynamic-text-option]');
        if (option) {
          option.classList.toggle('is-selected', input.checked);
        }
      });

      if (dynamicTextTopLine) {
        dynamicTextTopLine.hidden = mode !== 'one' && mode !== 'two';
      }

      if (dynamicTextBottomLine) {
        dynamicTextBottomLine.hidden = mode !== 'two';
      }

      if (dynamicTextNote) {
        dynamicTextNote.hidden = mode !== 'one' && mode !== 'two';
        dynamicTextNote.textContent = mode === 'two'
          ? 'A gravação superior ficará acima dos corações e a inferior ficará abaixo.'
          : 'A gravação ficará acima dos corações.';
      }
    }

    function resetDynamicTextState() {
      if (!dynamicTextControl) return;

      toArray(dynamicTextModeInputs).forEach(function (input) {
        input.checked = input.defaultChecked;
      });

      if (dynamicTextTopInput) dynamicTextTopInput.value = '';
      if (dynamicTextBottomInput) dynamicTextBottomInput.value = '';

      clearDynamicTextErrors();
      updateDynamicTextFields();
    }

    /* ------------------------------------------------------------------------
      6. Laser, dynamic text, variant plates, fingerprint and Morse collectors
    ------------------------------------------------------------------------ */

    function collectDynamicTextProperties(mainProperties) {
      var mode = getDynamicTextMode();
      var topValue = dynamicTextTopInput ? dynamicTextTopInput.value.trim() : '';
      var bottomValue = dynamicTextBottomInput ? dynamicTextBottomInput.value.trim() : '';

      clearDynamicTextErrors();

      if (!mode) {
        showLaserError('Selecione 1 ou 2 gravações.', laserBlock.querySelector('[data-pa-laser-field="dynamic-text"]'));
        return false;
      }

      if (mode === 'one') {
        if (!topValue) {
          setDynamicTextFieldError('top', 'Preencha a gravação superior ou selecione “Sem gravação”.');
          return false;
        }

        mainProperties[PROPERTY_NAMES.engravingTop] = topValue;
        return true;
      }

      if (mode === 'two') {
        if (!topValue) {
          setDynamicTextFieldError('top', 'Preencha a gravação superior.');
          return false;
        }

        if (!bottomValue) {
          setDynamicTextFieldError('bottom', 'Preencha a gravação inferior ou selecione “Uma gravação”.');
          return false;
        }

        mainProperties[PROPERTY_NAMES.engravingTop] = topValue;
        mainProperties[PROPERTY_NAMES.engravingBottom] = bottomValue;
        return true;
      }

      showLaserError('Selecione uma opção de gravação antes de continuar.', laserBlock.querySelector('[data-pa-laser-field="dynamic-text"]'));
      return false;
    }

    function getActiveVariant() {
      var activeVariantId = section.getAttribute('data-product-variant-id') || getActiveVariantIdFromPage();
      return findVariantById(activeVariantId);
    }

    function parsePlateCountFromValue(value) {
      var normalized = normalizeLaserText(value);
      var exactNumber = String(value || '').trim().match(/^[1-3]$/);
      var plateNumber = normalized.match(/\b([1-3])\s*(gravacao|gravacoes|chapa|chapas)\b/);

      if (exactNumber) return Number(exactNumber[0]);
      if (plateNumber) return Number(plateNumber[1]);

      return 0;
    }

    function getVariantPlateCount() {
      var activeVariant = getActiveVariant();
      var productOptions = productData && Array.isArray(productData.options) ? productData.options : [];
      var optionIndex = -1;
      var candidates = [];
      var count = 0;

      if (!activeVariant) return 1;

      productOptions.some(function (option, index) {
        var optionName = normalizeLaserText(option && option.name ? option.name : option);

        if (
          optionName.indexOf('gravacao') !== -1 ||
          optionName.indexOf('gravacoes') !== -1 ||
          optionName.indexOf('chapa') !== -1 ||
          optionName.indexOf('chapas') !== -1
        ) {
          optionIndex = index;
          return true;
        }

        return false;
      });

      if (Array.isArray(activeVariant.options) && optionIndex >= 0 && activeVariant.options[optionIndex]) {
        count = parsePlateCountFromValue(activeVariant.options[optionIndex]);
        if (count) return count;
      }

      ['option1', 'option2', 'option3'].forEach(function (optionKey) {
        if (activeVariant[optionKey]) {
          candidates.push(activeVariant[optionKey]);
        }
      });

      if (Array.isArray(activeVariant.options)) {
        candidates = candidates.concat(activeVariant.options);
      }

      candidates.some(function (candidate) {
        count = parsePlateCountFromValue(candidate);
        return !!count;
      });

      if (count) return count;

      String(activeVariant.title || '').split('/').some(function (titlePart) {
        count = parsePlateCountFromValue(titlePart.trim());
        return !!count;
      });

      return count || 1;
    }

    function getVariantPlateLine(plateNumber) {
      if (!variantPlatesControl) return null;
      return variantPlatesControl.querySelector('[data-pa-variant-plate-line="' + plateNumber + '"]');
    }

    function getVariantPlateInput(plateNumber) {
      if (!variantPlatesControl) return null;
      return variantPlatesControl.querySelector('[data-pa-variant-plate-input="' + plateNumber + '"]');
    }

    function setVariantPlateFieldError(plateNumber, message) {
      return setValidationState(
        getVariantPlateLine(plateNumber),
        '[data-pa-variant-plate-error]',
        '[data-pa-variant-plate-error-text]',
        message,
        { scroll: true }
      );
    }

    function clearVariantPlateFieldError(plateNumber) {
      setValidationState(
        getVariantPlateLine(plateNumber),
        '[data-pa-variant-plate-error]',
        '[data-pa-variant-plate-error-text]',
        '',
        { scroll: false }
      );
    }

    function clearVariantPlateErrors() {
      [1, 2, 3].forEach(clearVariantPlateFieldError);
    }

    function updateVariantPlateFields() {
      var plateCount;

      if (!variantPlatesControl) return;

      plateCount = getVariantPlateCount();

      toArray(variantPlateLines).forEach(function (line) {
        var plateNumber = Number(line.getAttribute('data-pa-variant-plate-line')) || 0;
        var input = line.querySelector('[data-pa-variant-plate-input]');

        line.hidden = plateNumber > plateCount;

        if (line.hidden && input) {
          input.value = '';
        }
      });

      if (variantPlatesCountLabel) {
        variantPlatesCountLabel.textContent = plateCount + (plateCount === 1 ? ' Memória' : ' Memórias');
      }
    }

    function resetVariantPlateState() {
      if (!variantPlatesControl) return;

      toArray(variantPlateInputs).forEach(function (input) {
        input.value = '';
      });

      clearVariantPlateErrors();
      updateVariantPlateFields();
    }

    function collectVariantPlateProperties(mainProperties) {
      var plateCount = getVariantPlateCount();
      var plateNumber;
      var input;
      var value;

      clearVariantPlateErrors();

      for (plateNumber = 1; plateNumber <= plateCount; plateNumber += 1) {
        input = getVariantPlateInput(plateNumber);
        value = input ? input.value.trim() : '';

        if (!value) {
          setVariantPlateFieldError(plateNumber, 'Preencha a memória ' + plateNumber + '.');
          return false;
        }

        mainProperties[PROPERTY_NAMES.engraving + ' ' + plateNumber] = value;
      }

      return true;
    }

    function parseFingerprintCountFromValue(value) {
      var normalized = normalizeLaserText(value);
      var exactNumber = String(value || '').trim().match(/^[1-2]$/);
      var engravingNumber = normalized.match(/\b([1-2])\s*(gravacao|gravacoes|impressao|impressoes)\b/);

      if (exactNumber) return Number(exactNumber[0]);
      if (engravingNumber) return Number(engravingNumber[1]);

      return 0;
    }

    function getFingerprintEngravingCount() {
      var activeVariant = getActiveVariant();
      var productOptions = productData && Array.isArray(productData.options) ? productData.options : [];
      var optionIndex = -1;
      var candidates = [];
      var count = 0;

      if (!fingerprintBlock) return 0;
      if (fingerprintBlock.getAttribute('data-pa-fingerprint-mode') === 'engraving') return 1;
      if (!activeVariant) return 1;

      productOptions.some(function (option, index) {
        var optionName = normalizeLaserText(option && option.name ? option.name : option);

        if (
          optionName.indexOf('gravacao') !== -1 ||
          optionName.indexOf('gravacoes') !== -1 ||
          optionName.indexOf('impressao') !== -1 ||
          optionName.indexOf('impressoes') !== -1
        ) {
          optionIndex = index;
          return true;
        }

        return false;
      });

      if (Array.isArray(activeVariant.options) && optionIndex >= 0 && activeVariant.options[optionIndex]) {
        count = parseFingerprintCountFromValue(activeVariant.options[optionIndex]);
        if (count) return count;
      }

      ['option1', 'option2', 'option3'].forEach(function (optionKey) {
        if (activeVariant[optionKey]) {
          candidates.push(activeVariant[optionKey]);
        }
      });

      if (Array.isArray(activeVariant.options)) {
        candidates = candidates.concat(activeVariant.options);
      }

      candidates.some(function (candidate) {
        count = parseFingerprintCountFromValue(candidate);
        return !!count;
      });

      if (count) return count;

      String(activeVariant.title || '').split('/').some(function (titlePart) {
        count = parseFingerprintCountFromValue(titlePart.trim());
        return !!count;
      });

      return count || 1;
    }

    function updateFingerprintState() {
      var engravingCount = getFingerprintEngravingCount();

      if (!fingerprintBlock) return;

      if (fingerprintCountLabel) {
        fingerprintCountLabel.textContent = engravingCount + (engravingCount === 1 ? ' impressão' : ' impressões');
      }

      toArray(fingerprintCountOptions).forEach(function (option) {
        var isSelected = Number(option.value) === engravingCount;
        var card = option.closest('[data-pa-fingerprint-choice-card]');

        option.checked = isSelected;
        if (card) card.classList.toggle('is-selected', isSelected);
      });

      if (fingerprintInstruction) {
        fingerprintInstruction.textContent = fingerprintBlock.getAttribute('data-pa-fingerprint-mode') === 'engraving'
          ? 'Pressione o dedo no carimbo e faça várias tentativas no cartão, sem o arrastar.'
          : engravingCount === 2
            ? 'Faça duas impressões nítidas, lado a lado.'
            : 'Faça uma impressão bem centrada numa das faces do molde.';
      }

      if (fingerprintDoubleNote) {
        fingerprintDoubleNote.hidden = engravingCount !== 2;
      }
    }

    function getFingerprintOptionIndex() {
      var productOptions = productData && Array.isArray(productData.options) ? productData.options : [];
      var optionIndex = -1;

      productOptions.some(function (option, index) {
        var optionName = normalizeLaserText(option && option.name ? option.name : option);

        if (
          optionName.indexOf('gravacao') !== -1 ||
          optionName.indexOf('gravacoes') !== -1 ||
          optionName.indexOf('impressao') !== -1 ||
          optionName.indexOf('impressoes') !== -1
        ) {
          optionIndex = index;
          return true;
        }

        return false;
      });

      return optionIndex;
    }

    function findFingerprintVariantForCount(engravingCount) {
      var activeVariant = getActiveVariant();
      var optionIndex = getFingerprintOptionIndex();
      var targetOptions;

      if (!activeVariant || optionIndex < 0 || !Array.isArray(activeVariant.options)) return null;

      targetOptions = activeVariant.options.slice();
      targetOptions[optionIndex] = String(engravingCount);

      return productData.variants.find(function (variant) {
        if (!variant || !Array.isArray(variant.options)) return false;

        return variant.options.every(function (value, index) {
          return String(value) === String(targetOptions[index]);
        });
      }) || null;
    }

    function selectFingerprintVariantCount(engravingCount) {
      var targetVariant = findFingerprintVariantForCount(engravingCount);
      var optionRadio;

      if (!targetVariant) return;

      optionRadio = qsa('input[type="radio"]', document).find(function (input) {
        var optionName = normalizeLaserText(input.name || '');

        return !section.contains(input)
          && String(input.value) === String(engravingCount)
          && (
            optionName.indexOf('gravacao') !== -1 ||
            optionName.indexOf('gravacoes') !== -1 ||
            optionName.indexOf('impressao') !== -1 ||
            optionName.indexOf('impressoes') !== -1
          );
      });

      if (optionRadio && !optionRadio.checked && typeof optionRadio.click === 'function') {
        optionRadio.click();
      }

      getMainProductForms().forEach(function (form) {
        var variantInput = form.querySelector('[name="id"]');

        if (!variantInput) return;
        variantInput.value = String(targetVariant.id);
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      section.setAttribute('data-product-variant-id', targetVariant.id);
      section.setAttribute('data-product-price', targetVariant.price);
      section.setAttribute('data-product-full-title', (section.getAttribute('data-product-title') || '') + ' - ' + targetVariant.title);
      basePrice = Number(targetVariant.price) || 0;
      mainProductTitle = section.getAttribute('data-product-full-title');

      updateDrawerProductSubtitle(section.getAttribute('data-product-title') || '', targetVariant.title);
      updateFingerprintState();
      updateTotal();
      scheduleMainProductSync(preferredProductForm);
    }

    function resetFingerprintState() {
      updateFingerprintState();
    }

    function collectFingerprintProperties(mainProperties) {
      var engravingCount = getFingerprintEngravingCount();

      if (!fingerprintBlock) return true;

      return true;
    }

    function addFingerprintKitProduct(cartItems) {
      var kitVariantId;
      var engravingCount;
      var fingerprintMode;
      var kitProperties;

      if (!fingerprintBlock) return;

      fingerprintMode = fingerprintBlock.getAttribute('data-pa-fingerprint-mode') || 'standalone';
      if (fingerprintMode === 'engraving' && section.getAttribute('data-pa-active-engraving') !== 'fingerprint') return;

      kitVariantId = fingerprintBlock.getAttribute('data-fingerprint-kit-variant-id');
      engravingCount = getFingerprintEngravingCount();

      if (!kitVariantId || Number.isNaN(Number(kitVariantId))) {
        console.warn('PA Drawer: variant ID do Kit de recolha inválido.', kitVariantId);
        return;
      }

      kitProperties = {
        [PROPERTY_NAMES.mainItem]: mainProductTitle,
      };

      if (fingerprintMode !== 'engraving') {
        kitProperties['_' + PROPERTY_NAMES.engravingCount] = String(engravingCount);
      }

      cartItems.push({
        id: Number(kitVariantId),
        quantity: 1,
        properties: kitProperties,
      });
    }

    var MORSE_MAP = {
      A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.',
      G: '--.', H: '....', I: '..', J: '.---', K: '-.-', L: '.-..',
      M: '--', N: '-.', O: '---', P: '.--.', Q: '--.-', R: '.-.',
      S: '...', T: '-', U: '..-', V: '...-', W: '.--', X: '-..-',
      Y: '-.--', Z: '--..',
      0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-',
      5: '.....', 6: '-....', 7: '--...', 8: '---..', 9: '----.',
    };

    function normalizeMorseSource(value) {
      return String(value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
    }

    function convertTextToMorse(value) {
      var normalizedValue = normalizeMorseSource(value);

      if (!normalizedValue) return '';

      return normalizedValue.split('').map(function (character) {
        if (character === ' ') return '/';
        return MORSE_MAP[character] || '';
      }).join(' ').replace(/\s+\/\s+/g, ' / ');
    }

    function hasInvalidMorseCharacters(value) {
      var normalizedValue = normalizeMorseSource(value);

      if (!normalizedValue) return false;

      return !/^[A-Z0-9 ]+$/.test(normalizedValue);
    }

    function setMorseFieldError(message) {
      return setValidationState(
        morseField,
        '[data-pa-morse-error]',
        '[data-pa-morse-error-text]',
        message,
        { scroll: true }
      );
    }

    function clearMorseError() {
      setValidationState(
        morseField,
        '[data-pa-morse-error]',
        '[data-pa-morse-error-text]',
        '',
        { scroll: false }
      );
    }

    function updateMorseVariantImage(variant) {
      var imageData;
      var imageUrl = '';

      if (!morseProductImage || !variant) return;

      imageData = variant.featured_image || variant.featured_media;

      if (typeof imageData === 'string') {
        imageUrl = imageData;
      } else if (imageData) {
        imageUrl = imageData.src || imageData.url || '';

        if (!imageUrl && imageData.preview_image) {
          imageUrl = imageData.preview_image.src || imageData.preview_image.url || '';
        }
      }

      if (!imageUrl) return;

      morseProductImage.removeAttribute('srcset');
      morseProductImage.src = imageUrl;
    }

    function updateMorsePreview() {
      var sourceValue = morseInput ? morseInput.value.trim() : '';
      var morseValue = '';

      if (!morseBlock) return;

      if (morseCounter) {
        morseCounter.textContent = String(sourceValue.length) + '/10 Max. caracteres';
        morseCounter.classList.toggle('has-value', sourceValue.length > 0);
        morseCounter.classList.toggle('is-at-limit', sourceValue.length >= 10);
      }

      if (!sourceValue || hasInvalidMorseCharacters(sourceValue)) {
        if (morseOutput) morseOutput.textContent = '';
        if (morsePreview) morsePreview.hidden = true;
        return;
      }

      morseValue = convertTextToMorse(sourceValue);

      if (morseOutput) {
        morseOutput.textContent = morseValue.replace(/\s\/\s/g, '   ');
        morseOutput.setAttribute('aria-label', morseValue);
      }

      if (morsePreview) {
        morsePreview.hidden = !morseValue;
      }
    }

    function scheduleMorsePreviewUpdate() {
      if (!morseInput) return;

      clearScopedTimeout(morseInputTimer);

      morseInputTimer = setScopedTimeout(function () {
        morseInputTimer = null;
        updateMorsePreview();
      }, 220);
    }

    function resetMorseState() {
      if (!morseBlock) return;

      if (morseInput) morseInput.value = '';
      if (morseOutput) morseOutput.textContent = '';
      if (morseCounter) {
        morseCounter.textContent = '0/10 Caracteres';
        morseCounter.classList.remove('has-value', 'is-at-limit');
      }
      if (morsePreview) morsePreview.hidden = true;

      updateMorseVariantImage(getActiveVariant());

      clearMorseError();
    }

    function collectMorseProperties(mainProperties) {
      var sourceValue;
      var morseValue;

      if (!morseBlock) return true;

      sourceValue = morseInput ? morseInput.value.trim() : '';

      clearMorseError();

      if (!sourceValue) {
        setMorseFieldError('Preencha o nome ou a palavra que pretende converter.');
        return false;
      }

      if (sourceValue.length > 10) {
        setMorseFieldError('O texto pode ter no máximo 10 caracteres.');
        return false;
      }

      if (hasInvalidMorseCharacters(sourceValue)) {
        setMorseFieldError('Utilize apenas letras, números e espaços.');
        return false;
      }

      morseValue = convertTextToMorse(sourceValue);

      if (!morseValue) {
        setMorseFieldError('Não foi possível converter este texto para código Morse.');
        return false;
      }

      mainProperties[PROPERTY_NAMES.morseSource] = sourceValue;
      mainProperties[PROPERTY_NAMES.morseCode] = morseValue;

      updateMorsePreview();

      return true;
    }

    function setStarMapStatus(message, isError) {
      if (!starMapStatus) return;

      starMapStatus.textContent = message || '';
      starMapStatus.hidden = !message;
      starMapStatus.classList.toggle('is-error', !!isError);
    }

    function updateStarMapNoteVisibility() {
      if (!starMapNote) return;

      var hasLocation = !!(
        starLatitudeInput && starLatitudeInput.value &&
        starLongitudeInput && starLongitudeInput.value
      );
      var isComplete = hasLocation && !!(
        starDateInput && starDateInput.value &&
        starTimeInput && starTimeInput.value
      );

      starMapNote.hidden = isComplete;
    }

    function setStarMapLocation(latitude, longitude, label) {
      var formattedLatitude = formatDirectionalCoordinate(latitude, true);
      var formattedLongitude = formatDirectionalCoordinate(longitude, false);
      var finalCoordinates = formattedLatitude + ', ' + formattedLongitude;

      if (starLatitudeInput) starLatitudeInput.value = latitude;
      if (starLongitudeInput) starLongitudeInput.value = longitude;
      if (starLocationDisplay) starLocationDisplay.value = label || '';
      if (starLocationVisual) starLocationVisual.textContent = label || '';
      if (starCoordinatesDisplay) starCoordinatesDisplay.value = finalCoordinates;
      if (starMapResult) starMapResult.hidden = false;

      setStarMapStatus('', false);
      updateStarMapNoteVisibility();
      clearStarMapFieldError('location');
      clearLaserError();
    }

    function resetStarMapState() {
      if (!starMapTool) return;

      abortSearchController(starSuggestionSearchController);
      starSuggestionSearchController = null;
      starSuggestionRequestId += 1;
      abortSearchController(starLocationSearchController);
      starLocationSearchController = null;
      starLocationSearchRequestId += 1;

      if (starLocationInput) starLocationInput.value = '';
      if (starDateInput) starDateInput.value = '';
      if (starTimeInput) starTimeInput.value = '';
      if (starLocationDisplay) starLocationDisplay.value = '';
      if (starLocationVisual) starLocationVisual.textContent = '';
      if (starCoordinatesDisplay) starCoordinatesDisplay.value = '';
      if (starLatitudeInput) starLatitudeInput.value = '';
      if (starLongitudeInput) starLongitudeInput.value = '';
      if (starMapResult) starMapResult.hidden = true;
      starLocationSuggestions = [];
      hideStarLocationDropdown();
      clearStarMapErrors();

      setStarMapStatus('', false);
      updateStarMapNoteVisibility();
    }

    function searchStarLocation() {
      if (!starLocationInput || !starLocationSearchButton) return;

      var query = starLocationInput.value.trim();
      var requestId;
      var fetchOptions = {
        headers: {
          Accept: 'application/json',
        },
      };
      var matchingSuggestion = starLocationSuggestions.find(function (result) {
        return getStarLocationLabel(result) === query;
      });

      abortSearchController(starLocationSearchController);
      starLocationSearchController = null;
      starLocationSearchRequestId += 1;
      requestId = starLocationSearchRequestId;

      if (!query) {
        setStarMapStatus('', false);
        setStarMapFieldError('location', 'Escreva uma localização antes de procurar.');
        return;
      }

      clearStarMapFieldError('location');

      if (matchingSuggestion) {
        selectStarLocationResult(matchingSuggestion);
        return;
      }

      starLocationSearchButton.classList.add('is-loading');
      starLocationSearchButton.disabled = true;
      setStarMapStatus('A procurar localização...', false);

      if (typeof AbortController === 'function') {
        starLocationSearchController = new AbortController();
        fetchOptions.signal = starLocationSearchController.signal;
      }

      fetch('https://nominatim.openstreetmap.org/search?format=json&limit=5&q=' + encodeURIComponent(query), fetchOptions)
        .then(function (response) {
          if (!response.ok) throw new Error('Erro ao procurar localização.');
          return response.json();
        })
        .then(function (results) {
          if (requestId !== starLocationSearchRequestId) return;

          if (!Array.isArray(results) || !results.length) {
            setStarMapStatus('', false);
            setStarMapFieldError('location', 'Não encontrámos esse local. Tente escrever a cidade, o país ou a morada completa.');
            renderStarLocationDropdown([]);
            return;
          }

          renderStarLocationDropdown(results);
          selectStarLocationResult(results[0]);
        })
        .catch(function (error) {
          if (isAbortError(error) || requestId !== starLocationSearchRequestId) return;

          setStarMapStatus('', false);
          setStarMapFieldError('location', 'Não foi possível procurar o local agora. Tente novamente dentro de instantes.');
        })
        .finally(function () {
          if (requestId === starLocationSearchRequestId) {
            starLocationSearchController = null;
            starLocationSearchButton.classList.remove('is-loading');
            starLocationSearchButton.disabled = false;
          }
        });
    }

    function formatStarMapDate(value) {
      if (!value) return '';

      var parts = value.split('-');
      if (parts.length !== 3) return value;

      return parts[2] + '/' + parts[1] + '/' + parts[0];
    }

    function getPropertyNameFromInputName(inputName) {
      var match = String(inputName || '').match(/^properties\[(.+)\]$/);
      return match ? match[1] : '';
    }

    function normalizeTeeInBlueUrl(value) {
      var url = String(value || '').trim().replace(/&amp;/g, '&');
      var match;

      if (!url || url.indexOf('teeinblue') === -1) return '';

      match = url.match(/(?:https?:\/\/|\/\/)?[^\s"'<>]*teeinblue[^\s"'<>]*/i);
      if (!match) return '';

      url = match[0];

      if (url.indexOf('//') === 0) {
        return 'https:' + url;
      }

      if (url.indexOf('http') !== 0) {
        return 'https://' + url;
      }

      return url;
    }

    function normalizeGeneratedTeeInBlueUrl(value) {
      var url = String(value || '').trim().replace(/&amp;/g, '&');
      var match;

      if (!url) return '';

      match = url.match(/https?:\/\/[^\s"'<>]+/i) || url.match(/\/\/[^\s"'<>]+/i);

      if (match) {
        url = match[0];
      }

      if (url.indexOf('//') === 0) {
        return 'https:' + url;
      }

      if (url.charAt(0) === '/') {
        return window.location.origin + url;
      }

      return url;
    }

    function collectKnownTeeInBlueProperties(mainProperties) {
      var knownPropertyNames = {
        _customization_id: true,
        _customization_image: true,
        _tib_design_link_1: true,
        _tib_design_link_2: true,
      };
      var designUrl = '';

      toArray(document.querySelectorAll('input[name^="properties["], textarea[name^="properties["]')).forEach(function (input) {
        var propertyName = getPropertyNameFromInputName(input.name);
        var propertyValue = input.value || '';

        if (!propertyName || !propertyValue) return;

        if (knownPropertyNames[propertyName]) {
          mainProperties[propertyName] = propertyValue;
        }

        if (!designUrl && propertyName.indexOf('_tib_design_link') === 0) {
          designUrl = normalizeTeeInBlueUrl(propertyValue) || propertyValue;
        }

        if (!designUrl && propertyName === '_customization_image') {
          designUrl = normalizeTeeInBlueUrl(propertyValue);
        }
      });

      if (!designUrl) {
        toArray(document.querySelectorAll('a[href*="teeinblue"], img[src*="teeinblue"], input[value*="teeinblue"], textarea')).some(function (element) {
          var candidate = '';

          if (element.href) candidate = normalizeTeeInBlueUrl(element.href);
          if (!candidate && element.src) candidate = normalizeTeeInBlueUrl(element.src);
          if (!candidate && typeof element.value !== 'undefined') candidate = normalizeTeeInBlueUrl(element.value);
          if (!candidate) candidate = normalizeTeeInBlueUrl(element.textContent);

          if (candidate) {
            designUrl = candidate;
            return true;
          }

          return false;
        });
      }

      if (designUrl) {
        mainProperties[PROPERTY_NAMES.designUrl] = designUrl;
      }
    }


    function getSelectedLaserType() {
      if (!laserBlock) return '';

      var selectedTypeInput = laserBlock.querySelector('[data-pa-laser-type]:checked');
      return selectedTypeInput ? selectedTypeInput.getAttribute('data-laser-type') : '';
    }

    function isStarLaserSelected() {
      return getSelectedLaserType() === 'star';
    }

    function dispatchNativeInputEvents(input) {
      if (!input) return;

      ['input', 'change', 'blur'].forEach(function (eventName) {
        input.dispatchEvent(
          new Event(eventName, {
            bubbles: true,
            cancelable: true,
          })
        );
      });
    }

    function setNativeInputValue(input, value) {
      var prototype;
      var descriptor;

      if (!input) return false;

      prototype = Object.getPrototypeOf(input);
      descriptor = prototype ? Object.getOwnPropertyDescriptor(prototype, 'value') : null;

      if (descriptor && typeof descriptor.set === 'function') {
        descriptor.set.call(input, value);
      } else {
        input.value = value;
      }

      dispatchNativeInputEvents(input);

      return true;
    }

    function buildStarMapDateTimeValue() {
      var dateValue = starDateInput ? starDateInput.value : '';
      var timeValue = starTimeInput ? starTimeInput.value : '';

      if (!dateValue || !timeValue) return '';

      return dateValue + 'T' + timeValue;
    }

    function getTeeInBlueRoot() {
      return (
        document.querySelector('#teeFormInputs') ||
        document.querySelector('.tee-customization-form') ||
        document.querySelector('.tee-form-inputs') ||
        document.querySelector('#teeinblue-wrapper') ||
        document.querySelector('.tee-shopify-product') ||
        document.querySelector('.teeinblue-item')
      );
    }

    function isTeeInBlueCampaignReady() {
      return !!(
        window.teeinblueCampaign &&
        window.teeinblueCampaign.isTeeInBlueProduct &&
        (window.TEEINBLUE_LOADED || document.querySelector('#teeinblue-wrapper'))
      );
    }

    function syncStarMapToTeeInBlue() {
      var teeRoot = getTeeInBlueRoot();
      var teeLocationInput = teeRoot ? teeRoot.querySelector('.tee-maps__input, input[placeholder*="localização" i], input[placeholder*="location" i]') : null;
      var teeDateTimeInput = teeRoot ? teeRoot.querySelector('.tee-timepicker__input, input[type="datetime-local"]') : null;
      var locationValue = starLocationInput ? starLocationInput.value.trim() : '';
      var dateTimeValue = buildStarMapDateTimeValue();
      var starField = laserBlock ? laserBlock.querySelector('[data-pa-laser-field="star"]') : null;

      if (teeLocationInput && teeDateTimeInput) {
        setNativeInputValue(teeLocationInput, locationValue);
        setNativeInputValue(teeDateTimeInput, dateTimeValue);

        setScopedTimeout(function () {
          dispatchNativeInputEvents(teeLocationInput);
          dispatchNativeInputEvents(teeDateTimeInput);
        }, 250);

        return true;
      }

      /*
        Some TeeInBlue star-map campaigns render only the artwork/mockup in the DOM
        and keep the personalization state inside the SDK, without exposing HTML inputs.
        In that setup we must still submit through the original product form so TeeInBlue
        can generate its private order properties during the native add-to-cart flow.
      */
      if (teeRoot && isTeeInBlueCampaignReady()) {
        console.info('PA Drawer: TeeInBlue campaign detected without editable DOM inputs. Continuing through native TeeInBlue submit flow.');
        return true;
      }

      showLaserError('O TeeInBlue está carregado, mas ainda não está pronto para gerar o mapa. Atualize a página e tente novamente.', starField);
      return false;
    }

    function findOriginalAddToCartButton() {
      return toArray(document.querySelectorAll(
        'button[data-add-to-cart].uploadkit-original-atc-button,' +
        'button[data-add-to-cart].textboxAddToCartBtn,' +
        'button[data-add-to-cart][name="add"],' +
        'button[type="submit"][name="add"]'
      )).find(function (button) {
        return !section.contains(button);
      }) || null;
    }

    function findOriginalProductForm(originalButton) {
      var form = originalButton ? originalButton.closest('form[action*="/cart/add"]') : null;

      if (form && !section.contains(form)) {
        return form;
      }

      return getMainProductForms()[0] || null;
    }

    function removeExistingProxyProperties(form) {
      if (!form) return;

      toArray(form.querySelectorAll('[data-pa-tee-proxy-property]')).forEach(function (input) {
        input.remove();
      });
    }

    function addProxyPropertyToForm(form, name, value) {
      var input;

      if (!form || !name || value === null || typeof value === 'undefined' || value === '') return;

      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      input.value = String(value);
      input.setAttribute('data-pa-tee-proxy-property', 'true');

      form.appendChild(input);
    }

    function injectPropertiesIntoOriginalForm(form, properties) {
      removeExistingProxyProperties(form);

      Object.keys(properties || {}).forEach(function (propertyName) {
        /*
          Do not inject TeeInBlue private properties manually here. Those must be
          generated by TeeInBlue itself during its native submit flow.
        */
        if (propertyName.indexOf('_tib_') === 0 || propertyName.indexOf('_customization_') === 0) {
          return;
        }

        addProxyPropertyToForm(form, propertyName, properties[propertyName]);
      });
    }

    function getStarMapTeeProductId() {
      if (window.teeinblueCampaign && window.teeinblueCampaign.productId) {
        return String(window.teeinblueCampaign.productId);
      }

      return section.getAttribute('data-product-id') || '';
    }

    function getStarMapTimezoneOffset(dateTimeValue) {
      var date = new Date(dateTimeValue);

      if (Number.isNaN(date.getTime())) {
        return -new Date().getTimezoneOffset();
      }

      return -date.getTimezoneOffset();
    }

    function getStarMapPlaceName(displayLocation, locationValue) {
      return displayLocation || locationValue || '';
    }

    function getStarMapShortName(displayLocation, locationValue) {
      var placeName = getStarMapPlaceName(displayLocation, locationValue);

      return placeName.split(',')[0].trim() || placeName;
    }

    /* Star map: build the exact TeeInBlue customization payload required by the campaign. */
    function buildTeeInBlueStarMapCustomization(variantId) {
      var locationValue = starLocationInput ? starLocationInput.value.trim() : '';
      var displayLocation = starLocationDisplay && starLocationDisplay.value ? starLocationDisplay.value.trim() : locationValue;
      var latitude = starLatitudeInput ? Number(starLatitudeInput.value) : NaN;
      var longitude = starLongitudeInput ? Number(starLongitudeInput.value) : NaN;
      var dateTimeValue = buildStarMapDateTimeValue();

      return {
        '982287': 'template-550818',
        'layer-835577': {
          lat: latitude,
          lng: longitude,
          name: getStarMapShortName(displayLocation, locationValue),
          place_name: getStarMapPlaceName(displayLocation, locationValue),
          time: dateTimeValue,
          timezone: getStarMapTimezoneOffset(dateTimeValue),
          custom_display: {},
        },
        platform_linked_id: String(variantId),
      };
    }

    function writeTeeInBlueCustomizationToStorage(productId, customization) {
      var storageKey = 'teeinblue-customizations';
      var items = [];
      var existingIndex;
      var nextItem;

      try {
        items = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      } catch (error) {
        items = [];
      }

      if (!Array.isArray(items)) {
        items = [];
      }

      existingIndex = items.findIndex(function (item) {
        return String(item && item.productId) === String(productId);
      });

      nextItem = {
        productId: String(productId),
        customization: customization,
        timestamp: Date.now(),
      };

      if (existingIndex >= 0) {
        items[existingIndex] = nextItem;
      } else {
        items.push(nextItem);
      }

      window.localStorage.setItem(storageKey, JSON.stringify(items));
    }

    function isTeeInBlueDirectImageUrl(url) {
      var normalizedUrl = String(url || '').toLowerCase();

      return (
        normalizedUrl.indexOf('/designs/') !== -1 &&
        /\.(png|jpg|jpeg|webp)(\?|$)/i.test(normalizedUrl)
      );
    }

    function buildTeeInBlueDesignImageUrl(customizationId) {
      var id = String(customizationId || '').trim();

      if (!id) return '';

      return 'https://cdn.teeinblue.com/designs/' + id + '/' + id + '-982287.png';
    }

    /* Star map: keep only the private TeeInBlue properties required by production. */
    function buildTeeInBlueGeneratedProperties(customizationResult) {
      var generatedProperties = {};
      var sourceProperties = customizationResult && customizationResult.properties;
      var imageUrl = '';
      var designLink = '';
      var customizationId = customizationResult && customizationResult.id ? String(customizationResult.id) : '';

      if (sourceProperties && typeof sourceProperties === 'object') {
        if (sourceProperties._customization_id) {
          customizationId = String(sourceProperties._customization_id);
        }

        if (sourceProperties._customization_image) {
          imageUrl = normalizeGeneratedTeeInBlueUrl(sourceProperties._customization_image);
        }

        if (sourceProperties._tib_design_link_1) {
          designLink = normalizeGeneratedTeeInBlueUrl(sourceProperties._tib_design_link_1);
        }
      }

      /*
        For production we keep the TeeInBlue customization id and use the direct
        high-resolution design image as _tib_design_link_1. If the SDK does not
        return that URL, build it from the customization id and the star-map
        template/artwork id 982287.
      */
      if (!designLink && customizationId) {
        designLink = buildTeeInBlueDesignImageUrl(customizationId);
      }

      if (customizationId) {
        generatedProperties._customization_id = customizationId;
      }

      if (imageUrl) {
        generatedProperties._customization_image = imageUrl;
      }

      if (designLink) {
        generatedProperties._tib_design_link_1 = designLink;
      }

      return generatedProperties;
    }

    function addStarMapCartItemsAfterTeeInBlue(customizationResult, mainProperties, variantId) {
      var generatedProperties = buildTeeInBlueGeneratedProperties(customizationResult);
      var cartItems = buildCartItems(variantId, Object.assign({}, mainProperties || {}, generatedProperties));

      return addCartItems(cartItems);
    }

    function submitStarMapThroughTeeInBlue(mainProperties, variantId) {
      var starField = laserBlock ? laserBlock.querySelector('[data-pa-laser-field="star"]') : null;
      var productId = getStarMapTeeProductId();
      var customization;

      if (!productId) {
        showLaserError('Não foi possível identificar o produto no TeeInBlue.', starField);
        return null;
      }

      if (!variantId) {
        showLaserError('Não foi possível identificar a variante selecionada.', starField);
        return null;
      }

      if (!window.teeinblue || typeof window.teeinblue.createCustomization !== 'function') {
        showLaserError('O TeeInBlue ainda não está pronto. Atualize a página e tente novamente.', starField);
        return null;
      }

      customization = buildTeeInBlueStarMapCustomization(variantId);
      writeTeeInBlueCustomizationToStorage(productId, customization);

      if (typeof window.teeinblue.setVariantId === 'function') {
        window.teeinblue.setVariantId(String(variantId));
      }

      if (typeof window.teeinblue.reinitCustomizationData === 'function') {
        window.teeinblue.reinitCustomizationData(String(productId));
      }

      return Promise.resolve()
        .then(function () {
          if (typeof window.teeinblue.refillCustomization === 'function') {
            window.teeinblue.refillCustomization(String(productId));
          }
        })
        .then(function () {
          return window.teeinblue.createCustomization();
        })
        .then(function (customizationResult) {
          return addStarMapCartItemsAfterTeeInBlue(customizationResult, mainProperties, variantId);
        })
        .catch(function (error) {
          console.error('PA Drawer: erro ao submeter mapa das estrelas via TeeInBlue.', error);
          throw error;
        });
    }

    /* Star map: validate each visible field independently before creating the TeeInBlue design. */
    function collectStarMapProperties(mainProperties, selectedTypeInput) {
      if (!starMapTool) {
        showLaserError('Não foi possível carregar os campos do mapa das estrelas. Atualize a página e tente novamente.');
        return false;
      }

      var starField = laserBlock.querySelector('[data-pa-laser-field="star"]');
      var locationValue = starLocationInput ? starLocationInput.value.trim() : '';
      var displayLocation = starLocationDisplay && starLocationDisplay.value ? starLocationDisplay.value : locationValue;
      var latitude = starLatitudeInput ? starLatitudeInput.value.trim() : '';
      var longitude = starLongitudeInput ? starLongitudeInput.value.trim() : '';
      var dateValue = starDateInput ? starDateInput.value : '';
      var timeValue = starTimeInput ? starTimeInput.value : '';

      clearStarMapErrors();
      setStarMapStatus('', false);

      if (!locationValue) {
        setStarMapFieldError('location', 'Insira a localização do momento que pretende eternizar.');
        return false;
      }

      if (!latitude || !longitude) {
        setStarMapFieldError('location', 'Confirme a localização antes de continuar.');
        return false;
      }

      if (!dateValue) {
        setStarMapFieldError('date', 'Selecione a data.');
        return false;
      }

      if (!timeValue) {
        setStarMapFieldError('time', 'Selecione a hora.');
        return false;
      }

      /*
        Do not inject TeeInBlue private properties here. For star maps, the drawer
        submits through TeeInBlue's native product form so TeeInBlue can generate
        _customization_id, _customization_image and _tib_design_link_1 itself.
      */
      return true;
    }

    function searchCoordinates() {
      if (!coordinateQueryInput || !coordinateSearchButton) return;

      var query = coordinateQueryInput.value.trim();
      var requestId;
      var fetchOptions = {
        headers: {
          Accept: 'application/json',
        },
      };

      abortSearchController(coordinateSearchController);
      coordinateSearchController = null;
      coordinateSearchRequestId += 1;
      requestId = coordinateSearchRequestId;

      if (!query) {
        setCoordinateStatus('Preencha primeiro a localização que pretende pesquisar.', true);
        return;
      }

      coordinateSearchButton.classList.add('is-loading');
      coordinateSearchButton.disabled = true;
      setCoordinateStatus('A procurar localização...', false);

      if (typeof AbortController === 'function') {
        coordinateSearchController = new AbortController();
        fetchOptions.signal = coordinateSearchController.signal;
      }

      fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(query), fetchOptions)
        .then(function (response) {
          if (!response.ok) throw new Error('Erro ao procurar localização.');
          return response.json();
        })
        .then(function (results) {
          if (requestId !== coordinateSearchRequestId) return;

          if (!Array.isArray(results) || !results.length) {
            setCoordinateStatus('Não encontrámos esse local. Tente escrever a cidade, o país ou a morada completa.', true);
            return;
          }

          setCoordinateOutput(results[0].lat, results[0].lon, false, getStarLocationLabel(results[0]));
        })
        .catch(function (error) {
          if (isAbortError(error) || requestId !== coordinateSearchRequestId) return;

          setCoordinateStatus('Não foi possível procurar o local agora. Tente novamente dentro de instantes.', true);
        })
        .finally(function () {
          if (requestId === coordinateSearchRequestId) {
            coordinateSearchController = null;
            coordinateSearchButton.classList.remove('is-loading');
            coordinateSearchButton.disabled = false;
          }
        });
    }

    function updateLaserFieldVisibility() {
      if (!laserBlock && !laserBackBlock) return;

      var selectedTypeInput = laserBlock ? laserBlock.querySelector('[data-pa-laser-type]:checked') : null;
      var selectedType = selectedTypeInput ? selectedTypeInput.getAttribute('data-laser-type') : '';

      toArray(laserOptionInputs).forEach(function (input) {
        var card = input.closest('[data-pa-laser-option]');
        if (card) {
          card.classList.toggle('is-selected', input.checked);
        }
      });

      toArray(laserFields).forEach(function (field) {
        var fieldType = field.getAttribute('data-pa-laser-field');

        if (fieldType === 'default') {
          field.hidden = false;
          return;
        }

        field.hidden = fieldType !== selectedType;
      });

      var backToggle = laserBackBlock ? laserBackBlock.querySelector('[data-pa-laser-back-toggle]') : null;
      var backField = laserBackBlock ? laserBackBlock.querySelector('[data-pa-laser-back-field]') : null;

      if (backField) {
        backField.hidden = !(backToggle && backToggle.checked);
      }

      if (laserBackCard) {
        laserBackCard.classList.toggle('is-active', !!(backToggle && backToggle.checked));
      }

      updateDynamicTextFields();
      updateVariantPlateFields();

      if (selectedType === 'coordenadas' && coordinateMap) {
        setScopedTimeout(function () {
          coordinateMap.invalidateSize();
        }, 150);
      }
    }

    function getVisibleLaserField() {
      if (!laserBlock) return null;

      return toArray(laserBlock.querySelectorAll('[data-pa-laser-field]')).find(function (field) {
        return !field.hidden;
      }) || null;
    }

    function normalizeLaserText(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }

    function getEmptyLaserFieldMessage(selectedType, propertyName) {
      var normalizedType = normalizeLaserText(selectedType);
      var normalizedProperty = normalizeLaserText(propertyName);
      var combined = normalizedType + ' ' + normalizedProperty;

      if (combined.indexOf('inicial') !== -1) {
        return 'Preencha a inicial que pretende gravar.';
      }

      if (combined.indexOf('nome') !== -1) {
        return 'Preencha o nome que pretende gravar.';
      }

      if (combined.indexOf('data') !== -1) {
        return 'Preencha a data que pretende gravar.';
      }

      if (combined.indexOf('coordenada') !== -1 || combined.indexOf('localizacao') !== -1 || combined.indexOf('local') !== -1) {
        return 'Selecione uma localização antes de continuar.';
      }

      if (combined.indexOf('morse') !== -1) {
        return 'Preencha a mensagem que pretende converter em código Morse.';
      }

      if (combined.indexOf('frase') !== -1 || combined.indexOf('mensagem') !== -1) {
        return 'Preencha a frase ou mensagem que pretende gravar.';
      }

      return 'Preencha o texto que pretende gravar.';
    }

    function collectLaserProperties(mainProperties) {
      if (!laserBlock) return true;

      /* Fingerprint engraving has its own kit workflow and no laser text/upload field. */
      if (section.getAttribute('data-pa-active-engraving') === 'fingerprint') {
        clearLaserError();
        return true;
      }

      clearLaserError();

      var defaultField = laserBlock.querySelector('[data-pa-laser-field="default"]');
      var selectedTypeInput = laserBlock.querySelector('[data-pa-laser-type]:checked');
      var selectedType = selectedTypeInput ? selectedTypeInput.getAttribute('data-laser-type') : '';
      var visibleField = getVisibleLaserField();

      if (!defaultField && !selectedTypeInput) {
        showLaserError('Selecione o tipo de gravação.');
        return false;
      }

      /*
        Laser photo uses UploadKit's original generated properties.
        Do not add custom photo labels here. This keeps cart, checkout and order display aligned with UploadKit.
        The function does not return early because the optional back engraving validation still has to run.
      */
      if (selectedType === 'star') {
        if (!collectStarMapProperties(mainProperties, selectedTypeInput)) {
          return false;
        }
      } else if (selectedType === 'dynamic-text') {
        if (!collectDynamicTextProperties(mainProperties)) {
          return false;
        }
      } else if (selectedType === 'variant-plates') {
        if (!collectVariantPlateProperties(mainProperties)) {
          return false;
        }
      } else if (selectedType === 'photo') {
        var laserPhotoProperties = getUploadKitProperties(laserPhotoUploadkit);

        if (!laserPhotoProperties.length) {
          if (laserPhotoUploadkit) {
            laserPhotoUploadkit.classList.add('has-error');
          }

          showLaserError('Carregue a imagem que pretende gravar.');
          return false;
        }

        if (laserPhotoUploadkit) {
          laserPhotoUploadkit.classList.remove('has-error');
        }

        laserPhotoProperties.forEach(function (property) {
          mainProperties[property.name] = property.value;
        });
      } else {

        var laserInput = visibleField ? visibleField.querySelector('[data-pa-laser-input]') : null;
        var laserValue = laserInput ? laserInput.value.trim() : '';
        var propertyName = laserInput ? laserInput.getAttribute('data-laser-property-name') : '';

        if (selectedType === 'coordenadas') {
          propertyName = 'Coordenadas';
        }

        if (!laserValue) {
          showLaserError(getEmptyLaserFieldMessage(selectedType, propertyName), visibleField);
          return false;
        }

        mainProperties[propertyName || PROPERTY_NAMES.defaultLaserText] = laserValue;
      }

      return true;
    }

    function collectLaserBackProperties(mainProperties) {
      var backToggle = section.querySelector('[data-pa-laser-back-toggle]');
      var backInput = section.querySelector('[data-pa-laser-back-input]');
      var backField = section.querySelector('[data-pa-laser-back-field]');

      if (!laserBackCard || !backToggle || !backToggle.checked) {
        return true;
      }

      var backValue = backInput ? backInput.value.trim() : '';
      var backAvailable = laserBackCard.getAttribute('data-laser-back-available') === 'true';

      if (!backAvailable) {
        showLaserError('A gravação traseira não está disponível neste momento.', backField);
        return false;
      }

      if (!backValue) {
        showLaserError('Preencha o texto da gravação traseira.', backField);
        return false;
      }

      mainProperties[PROPERTY_NAMES.engravingBack] = backValue;
      mainProperties._pa_link_id = createLaserBackLinkId();

      return true;
    }

    function resetLaserState() {
      if (!laserBlock && !laserBackBlock) return;

      toArray(laserOptionInputs).forEach(function (input) {
        input.checked = input.defaultChecked;
      });

      toArray(section.querySelectorAll('[data-pa-laser-input], [data-pa-laser-back-input], [data-pa-dynamic-text-input], [data-pa-variant-plate-input]')).forEach(function (input) {
        input.value = '';
      });

      var backToggle = section.querySelector('[data-pa-laser-back-toggle]');
      if (backToggle) {
        backToggle.checked = false;
      }

      resetCoordinateFinder();
      resetStarMapState();
      resetDynamicTextState();
      resetVariantPlateState();
      clearLaserError();
      updateLaserFieldVisibility();
    }

    var initialExtraSelectionState = toArray(extraCards).map(function (card) {
      return card.classList.contains('is-selected');
    });

    function formatMoney(cents) {
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, moneyFormat);
        }
      } catch (error) { }

      return (cents / 100).toLocaleString('pt-PT', {
        style: 'currency',
      currency: 'EUR',
      });
    }

    /* ------------------------------------------------------------------------
      7. Drawer, modals, keyboard handling and totals
    ------------------------------------------------------------------------ */

    var engravingLabels = {
      coordinates: 'Coordenadas',
      text: 'Palavra ou mensagem',
      fingerprint: 'Impressão digital',
      'star-map': 'Mapa das estrelas',
      artwork: 'Desenho',
    };

    var engravingLaserTypes = {
      coordinates: 'coordenadas',
      text: 'nome',
      fingerprint: '',
      'star-map': 'star',
      artwork: 'photo',
    };

    function getSelectedEngravingChoice() {
      return engravingSelector ? engravingSelector.querySelector('[data-pa-engraving-choice]:checked') : null;
    }

    function clearEngravingError() {
      if (!engravingSelector) return;

      var error = section.querySelector('[data-pa-engraving-error]');
      var errorText = section.querySelector('[data-pa-engraving-error-text]');

      engravingSelector.classList.remove('has-error');
      if (error) error.hidden = true;
      if (errorText) errorText.textContent = '';
    }

    function showEngravingError(message) {
      if (!engravingSelector) return;

      var error = section.querySelector('[data-pa-engraving-error]');
      var errorText = section.querySelector('[data-pa-engraving-error-text]');

      engravingSelector.classList.add('has-error');
      if (errorText) errorText.textContent = message;
      if (error) error.hidden = false;
    }

    function updateEngravingChoiceCards() {
      toArray(engravingChoiceInputs).forEach(function (input) {
        var card = input.closest('[data-pa-engraving-option]');
        if (card) card.classList.toggle('is-selected', input.checked);
      });

      clearEngravingError();

      if (submitButton && engravingSelector && !engravingSelector.hidden) {
        submitButton.disabled = false;
      }
    }

    function selectEngravingChoice(input) {
      if (!input || input.checked) {
        updateEngravingChoiceCards();
        return;
      }

      input.checked = true;
      updateEngravingChoiceCards();
    }

    function setEngravingStep(step) {
      if (!engravingSelector || !engravingDetail) return;

      var isSelectionStep = step !== 'detail';

      engravingSelector.hidden = !isSelectionStep;
      engravingDetail.hidden = isSelectionStep;
      section.setAttribute('data-pa-engraving-step', isSelectionStep ? 'selection' : 'detail');

      if (submitButton) {
        submitButton.textContent = isSelectionStep ? 'Continuar' : submitDefaultText;
        submitButton.disabled = false;
      }

      if (isSelectionStep) {
        resetDrawerBodyScroll();

        var selectedEngravingChoice = getSelectedEngravingChoice();
        if (selectedEngravingChoice) {
          focusFirstInteractiveElement(engravingSelector, selectedEngravingChoice);
        }
      } else {
        resetDrawerBodyScroll();
        focusFirstInteractiveElement(engravingDetail, engravingBackButton);
      }
    }

    function resetDrawerBodyScroll() {
      if (!drawerBody) return;

      drawerBody.scrollTop = 0;

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () {
          drawerBody.scrollTop = 0;
        });
      }
    }

    function continueEngravingFlow() {
      var selectedChoice = getSelectedEngravingChoice();
      var engravingKey = selectedChoice ? selectedChoice.value : '';
      var targetLaserType = engravingLaserTypes[engravingKey];
      var targetLaserInput = targetLaserType && laserBlock
        ? laserBlock.querySelector('[data-pa-laser-type][data-laser-type="' + targetLaserType + '"]')
        : null;

      if (!selectedChoice || (engravingKey !== 'fingerprint' && !targetLaserInput)) {
        showEngravingError('Selecione o tipo de personalização antes de continuar.');
        return false;
      }

      toArray(laserOptionInputs).forEach(function (input) {
        input.checked = false;
      });

      if (targetLaserInput) {
        targetLaserInput.checked = true;
      }
      section.setAttribute('data-pa-active-engraving', engravingKey);

      if (engravingDetailTitle) {
        engravingDetailTitle.textContent = engravingLabels[engravingKey] || 'Personalização';
      }

      if (engravingPhotoIntro) {
        engravingPhotoIntro.hidden = engravingKey !== 'artwork';
      }

      if (engravingKey === 'artwork') {
        if (engravingPhotoTitle) engravingPhotoTitle.textContent = 'Importar desenho';
        ensureLaserPhotoUploadkit();
      }

      updateLaserFieldVisibility();
      clearLaserError();
      setEngravingStep('detail');
      return true;
    }

    if (engravingSelector) {
      toArray(engravingChoiceInputs).forEach(function (input) {
        var card = input.closest('[data-pa-engraving-option]');
        var touchStartX = 0;
        var touchStartY = 0;
        var touchMoved = false;

        addScopedEventListener(input, 'change', updateEngravingChoiceCards);

        if (card) {
          addScopedEventListener(card, 'pointerdown', function (event) {
            if (event.button && event.button !== 0) return;
            touchStartX = event.clientX || 0;
            touchStartY = event.clientY || 0;
            touchMoved = false;
          }, { passive: true });

          addScopedEventListener(card, 'pointermove', function (event) {
            var deltaX = Math.abs((event.clientX || 0) - touchStartX);
            var deltaY = Math.abs((event.clientY || 0) - touchStartY);

            if (deltaX > 8 || deltaY > 8) {
              touchMoved = true;
            }
          }, { passive: true });

          addScopedEventListener(card, 'click', function (event) {
            if (touchMoved) {
              event.preventDefault();
              touchMoved = false;
              return;
            }

            selectEngravingChoice(input);
          });
        }
      });

      if (engravingBackButton) {
        addScopedEventListener(engravingBackButton, 'click', function () {
          setEngravingStep('selection');
        });
      }

      updateEngravingChoiceCards();
    }

    function openDrawer(openButton) {
      lastOpenButton = openButton || lastOpenButton;
      preferredProductForm = getPreferredProductFormFromOpenButton(lastOpenButton);

      scheduleMainProductSync(preferredProductForm);
      updateTotal();
      clearSubmitError();

      if (engravingSelector) {
        toArray(engravingChoiceInputs).forEach(function (input) {
          input.checked = false;
        });
        section.removeAttribute('data-pa-active-engraving');
        updateEngravingChoiceCards();
        setEngravingStep('selection');
      }

      document.body.classList.add('pa-personalization-open');
      setFloatingChatVisibility(true);
      setScopedTimeout(function () {
        if (isDrawerOpen()) setFloatingChatVisibility(true);
      }, 250);
      setScopedTimeout(function () {
        if (isDrawerOpen()) setFloatingChatVisibility(true);
      }, 900);
      setScopedTimeout(function () {
        if (isDrawerOpen()) setFloatingChatVisibility(true);
      }, 1800);
      syncOpenButtonsState(true);

      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
      }

      focusFirstInteractiveElement(drawer, section.querySelector('[data-pa-personalization-close]'));
    }

    function closeDrawer() {
      if (!isDrawerOpen()) return;

      section.classList.remove('pa-addon-info-open');
      document.body.classList.remove('pa-personalization-open');
      setFloatingChatVisibility(false);
      syncOpenButtonsState(false);

      if (drawer) {
        drawer.setAttribute('aria-hidden', 'true');
      }

      if (lastOpenButton && typeof lastOpenButton.focus === 'function') {
        lastOpenButton.focus({ preventScroll: true });
      }
    }

    var photoGuideModal = section.querySelector('[data-pa-photo-guide-modal]');
    var photoGuideOpenButtons = section.querySelectorAll('[data-pa-open-photo-guide]');
    var photoGuideCloseButton = section.querySelector('[data-pa-close-photo-guide]');
    var photoGuideConfirmButton = section.querySelector('[data-pa-confirm-photo-guide]');

    var laserGuideModal = section.querySelector('[data-pa-laser-guide-modal]');
    var laserGuideOpenButtons = section.querySelectorAll('[data-pa-open-laser-guide]');
    var laserGuideCloseButton = section.querySelector('[data-pa-close-laser-guide]');
    var laserGuideConfirmButton = section.querySelector('[data-pa-confirm-laser-guide]');

    var fingerprintGuideModal = section.querySelector('[data-pa-fingerprint-guide-modal]');
    var fingerprintGuideOpenButtons = section.querySelectorAll('[data-pa-open-fingerprint-guide]');
    var fingerprintGuideCloseButton = section.querySelector('[data-pa-close-fingerprint-guide]');
    var fingerprintGuideConfirmButton = section.querySelector('[data-pa-confirm-fingerprint-guide]');

    var addonInfoModal = section.querySelector('[data-pa-addon-info-modal]');
    var addonInfoOpenButtons = section.querySelectorAll('[data-pa-addon-info-open]');
    var addonInfoCloseButton = section.querySelector('[data-pa-addon-info-close]');
    var addonInfoTitle = section.querySelector('[data-pa-addon-info-title]');
    var addonInfoPrice = section.querySelector('[data-pa-addon-info-price]');
    var addonInfoDescription = section.querySelector('[data-pa-addon-info-description]');
    var addonInfoImageWrap = section.querySelector('[data-pa-addon-info-image-wrap]');
    var addonInfoImage = section.querySelector('[data-pa-addon-info-image]');

    function openModal(modal, openButton) {
      if (!modal) return;

      activeModal = modal;
      lastModalOpenButton = openButton || lastModalOpenButton;

      modal.classList.add('is-active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      focusFirstInteractiveElement(modal, qs('[data-pa-close-photo-guide], [data-pa-close-laser-guide], [data-pa-close-fingerprint-guide], [data-pa-addon-info-close]', modal));
    }

    function closeModal(modal) {
      if (!modal) return;

      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');

      if (activeModal === modal) {
        activeModal = null;
      }

      document.body.style.overflow = '';

      if (lastModalOpenButton && typeof lastModalOpenButton.focus === 'function') {
        lastModalOpenButton.focus({ preventScroll: true });
      }
    }

    function openPhotoGuideModal(openButton) {
      openModal(photoGuideModal, openButton);
    }

    function closePhotoGuideModal() {
      closeModal(photoGuideModal);
    }

    function openLaserGuideModal(openButton) {
      openModal(laserGuideModal, openButton);
    }

    function closeLaserGuideModal() {
      closeModal(laserGuideModal);
    }

    function openFingerprintGuideModal(openButton) {
      openModal(fingerprintGuideModal, openButton);
    }

    function closeFingerprintGuideModal() {
      closeModal(fingerprintGuideModal);
    }

    function openAddonInfoModal(openButton) {
      var title = openButton ? openButton.getAttribute('data-addon-info-title') : '';
      var price = openButton ? openButton.getAttribute('data-addon-info-price') : '';
      var description = openButton ? openButton.getAttribute('data-addon-info-description') : '';
      var imageUrl = openButton ? openButton.getAttribute('data-addon-info-image') : '';
      var imageAlt = openButton ? openButton.getAttribute('data-addon-info-image-alt') : '';

      if (!addonInfoModal) return;

      if (addonInfoTitle) addonInfoTitle.textContent = title || '';
      if (addonInfoPrice) addonInfoPrice.textContent = price || '';
      if (addonInfoDescription) addonInfoDescription.textContent = description || '';

      if (addonInfoImage && addonInfoImageWrap && imageUrl) {
        addonInfoImage.src = imageUrl;
        addonInfoImage.alt = imageAlt || title || '';
        addonInfoImageWrap.hidden = false;
      } else if (addonInfoImage && addonInfoImageWrap) {
        addonInfoImage.removeAttribute('src');
        addonInfoImage.alt = '';
        addonInfoImageWrap.hidden = true;
      }

      section.classList.add('pa-addon-info-open');
      openModal(addonInfoModal, openButton);
    }

    function closeAddonInfoModal() {
      section.classList.remove('pa-addon-info-open');
      closeModal(addonInfoModal);
    }

    function setFloatingChatVisibility(isHidden) {
      var chatSelectors = [
        '.woot-widget-bubble',
        '.woot-widget-holder',
        '[id*="commslayer" i]',
        '[class*="commslayer" i]',
        'iframe[src*="commslayer" i]'
      ];

      qsa(chatSelectors.join(',')).forEach(function (element) {
        if (isHidden) {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('opacity', '0', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        } else {
          element.style.removeProperty('display');
          element.style.removeProperty('opacity');
          element.style.removeProperty('visibility');
          element.style.removeProperty('pointer-events');
        }
      });

    }

    toArray(photoGuideOpenButtons).forEach(function (button) {
      addScopedEventListener(button, 'click', function (event) {
        event.preventDefault();
        openPhotoGuideModal(event.currentTarget);
      });
    });

    if (photoGuideCloseButton) addScopedEventListener(photoGuideCloseButton, 'click', closePhotoGuideModal);
    if (photoGuideConfirmButton) addScopedEventListener(photoGuideConfirmButton, 'click', closePhotoGuideModal);

    if (photoGuideModal) {
      addScopedEventListener(photoGuideModal, 'click', function (event) {
        if (event.target === photoGuideModal) {
          closePhotoGuideModal();
        }
      });
    }

    toArray(laserGuideOpenButtons).forEach(function (button) {
      addScopedEventListener(button, 'click', function (event) {
        event.preventDefault();
        openLaserGuideModal(event.currentTarget);
      });
    });

    if (laserGuideCloseButton) addScopedEventListener(laserGuideCloseButton, 'click', closeLaserGuideModal);
    if (laserGuideConfirmButton) addScopedEventListener(laserGuideConfirmButton, 'click', closeLaserGuideModal);

    if (laserGuideModal) {
      addScopedEventListener(laserGuideModal, 'click', function (event) {
        if (event.target === laserGuideModal) {
          closeLaserGuideModal();
        }
      });
    }

    toArray(fingerprintGuideOpenButtons).forEach(function (button) {
      addScopedEventListener(button, 'click', function (event) {
        event.preventDefault();
        openFingerprintGuideModal(event.currentTarget);
      });
    });

    if (fingerprintGuideCloseButton) addScopedEventListener(fingerprintGuideCloseButton, 'click', closeFingerprintGuideModal);
    if (fingerprintGuideConfirmButton) addScopedEventListener(fingerprintGuideConfirmButton, 'click', closeFingerprintGuideModal);

    if (fingerprintGuideModal) {
      addScopedEventListener(fingerprintGuideModal, 'click', function (event) {
        if (event.target === fingerprintGuideModal) {
          closeFingerprintGuideModal();
        }
      });
    }

    toArray(fingerprintCountOptions).forEach(function (option) {
      addScopedEventListener(option, 'change', function () {
        if (!option.checked) return;
        selectFingerprintVariantCount(Number(option.value));
      });
    });

    toArray(addonInfoOpenButtons).forEach(function (button) {
      addScopedEventListener(button, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openAddonInfoModal(event.currentTarget);
      });

      addScopedEventListener(button, 'keydown', function (event) {
        event.stopPropagation();
      });
    });

    if (addonInfoCloseButton) addScopedEventListener(addonInfoCloseButton, 'click', closeAddonInfoModal);

    if (addonInfoModal) {
      addScopedEventListener(addonInfoModal, 'click', function (event) {
        if (event.target === addonInfoModal) {
          closeAddonInfoModal();
        }
      });
    }

    if (coordinateSearchButton) {
      addScopedEventListener(coordinateSearchButton, 'click', searchCoordinates);
    }

    if (coordinateChangeButton && coordinateQueryInput) {
      addScopedEventListener(coordinateChangeButton, 'click', function () {
        coordinateQueryInput.focus();
        coordinateQueryInput.select();
      });
    }

    if (coordinateQueryInput) {
      addScopedEventListener(coordinateQueryInput, 'keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          searchCoordinates();
        }
      });
    }

    if (starLocationSearchButton) {
      addScopedEventListener(starLocationSearchButton, 'click', searchStarLocation);
    }

    if (starLocationDropdown) {
      addScopedEventListener(starLocationDropdown, 'click', function (event) {
        var optionButton = event.target.closest('[data-pa-star-location-option]');
        var optionIndex;

        if (!optionButton) return;

        optionIndex = Number(optionButton.getAttribute('data-pa-star-location-option'));
        selectStarLocationResult(starLocationSuggestions[optionIndex]);
      });
    }

    if (starLocationInput) {
      addScopedEventListener(starLocationInput, 'keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          searchStarLocation();
        }

        if (event.key === 'Escape') {
          hideStarLocationDropdown();
        }
      });

      addScopedEventListener(starLocationInput, 'input', function () {
        if (starLatitudeInput) starLatitudeInput.value = '';
        if (starLongitudeInput) starLongitudeInput.value = '';
        if (starLocationDisplay) starLocationDisplay.value = '';
        if (starLocationVisual) starLocationVisual.textContent = '';
        if (starCoordinatesDisplay) starCoordinatesDisplay.value = '';
        if (starMapResult) starMapResult.hidden = true;
        setStarMapStatus('', false);
        updateStarMapNoteVisibility();
        clearStarMapFieldError('location');
        scheduleStarLocationSuggestions();
        clearLaserError();
      });

      addScopedEventListener(starLocationInput, 'blur', function () {
        setScopedTimeout(hideStarLocationDropdown, 160);
      });
    }

    if (starDateInput) {
      addScopedEventListener(starDateInput, 'change', function () {
        clearStarMapFieldError('date');
        clearLaserError();
        updateStarMapNoteVisibility();
      });
    }

    if (starTimeInput) {
      addScopedEventListener(starTimeInput, 'change', function () {
        clearStarMapFieldError('time');
        clearLaserError();
        updateStarMapNoteVisibility();
      });
    }

    if (morseInput) {
      addScopedEventListener(morseInput, 'input', function () {
        var maxChars = Number(morseInput.getAttribute('data-max-chars')) || 0;

        if (maxChars > 0) {
          morseInput.value = morseInput.value.slice(0, maxChars);
        }

        clearMorseError();
        scheduleMorsePreviewUpdate();
      });

      addScopedEventListener(morseInput, 'change', function () {
        clearMorseError();
        updateMorsePreview();
      });

      addScopedEventListener(morseInput, 'blur', updateMorsePreview);
    }

    if (laserBlock) {
      addScopedEventListener(laserBlock, 'change', function () {
        clearLaserError();
        updateLaserFieldVisibility();
        updateTotal();
      });

      addScopedEventListener(laserBlock, 'input', function (event) {
        var target = event.target;
        if (!target) return;

        var maxChars = Number(target.getAttribute('data-max-chars')) || 0;
        var transform = target.getAttribute('data-transform');

        if (target.getAttribute('data-letters-only') === 'true') {
          target.value = target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, '');
        }

        if (transform === 'uppercase') {
          target.value = target.value.toUpperCase();
        }

        if (maxChars > 0) {
          target.value = target.value.slice(0, maxChars);
        }

        clearLaserError();
      });

      updateLaserFieldVisibility();
    }

    if (laserBackBlock) {
      addScopedEventListener(laserBackBlock, 'change', function () {
        clearLaserError();
        updateLaserFieldVisibility();
        updateTotal();
      });

      addScopedEventListener(laserBackBlock, 'input', function (event) {
        var target = event.target;
        if (!target) return;

        var maxChars = Number(target.getAttribute('data-max-chars')) || 0;

        if (maxChars > 0) {
          target.value = target.value.slice(0, maxChars);
        }

        clearLaserError();
      });

      updateLaserFieldVisibility();
    }

    addScopedEventListener(section, 'input', function (event) {
      var target = event.target;
      var errorScope;

      if (!target) return;

      errorScope = target.closest('[data-pa-laser-field], [data-pa-laser-back-field], [data-pa-morse-field], [data-pa-variant-plate-line], [data-pa-dynamic-text-line], .pa-uploadkit-block');

      if (errorScope) {
        errorScope.classList.remove('has-error');
      }

      if (target.closest('[data-pa-laser-block], [data-pa-laser-back-block]')) {
        clearLaserError();
      }
    });

    addScopedEventListener(section, 'change', function (event) {
      var target = event.target;

      if (!target) return;

      if (target.closest('[data-pa-laser-block], [data-pa-laser-back-block]')) {
        clearLaserError();
      }
    });

    updateFingerprintState();

    function updateTotal() {
      var total = basePrice;

      toArray(addonCards).forEach(function (card) {
        if (card.classList.contains('is-selected')) {
          total += Number(card.getAttribute('data-addon-price')) || 0;
        }
      });

      total += getSelectedExtrasTotal();

      if (extrasBundleActive) {
        total -= getExtrasBundleDiscountAmount();
      }

      if (laserBackCard) {
        var backToggle = laserBackCard.querySelector('[data-pa-laser-back-toggle]');
        var backAvailable = laserBackCard.getAttribute('data-laser-back-available') === 'true';

        if (backToggle && backToggle.checked && backAvailable) {
          total += Number(laserBackCard.getAttribute('data-laser-back-price')) || 0;
        }
      }

      if (totalEl) {
        totalEl.textContent = formatMoney(total);
      }
    }

    qsa('[data-pa-personalization-open]').forEach(function (button) {
      addScopedEventListener(button, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openDrawer(button);
      });
    });

    addScopedEventListener(document, 'click', function (event) {
      var openButton = event.target.closest('[data-pa-personalization-open]');
      var closeButton = event.target.closest('[data-pa-personalization-close]');

      if (openButton) {
        event.preventDefault();
        openDrawer(openButton);
      }

      if (closeButton && section.contains(closeButton)) {
        event.preventDefault();
        closeDrawer();
      }
    });

    addScopedEventListener(document, 'keydown', function (event) {
      var activePhotoGuideModal = section.querySelector('[data-pa-photo-guide-modal].is-active');
      var activeLaserGuideModal = section.querySelector('[data-pa-laser-guide-modal].is-active');
      var activeFingerprintGuideModal = section.querySelector('[data-pa-fingerprint-guide-modal].is-active');
      var activeAddonInfoModal = section.querySelector('[data-pa-addon-info-modal].is-active');
      var modalToTrap = activePhotoGuideModal || activeLaserGuideModal || activeFingerprintGuideModal || activeAddonInfoModal || activeModal;

      if (event.key === 'Tab') {
        if (modalToTrap) {
          trapFocus(event, modalToTrap);
          return;
        }

        if (isDrawerOpen()) {
          trapFocus(event, drawer);
        }

        return;
      }

      if (event.key !== 'Escape') return;

      if (activePhotoGuideModal) {
        closePhotoGuideModal();
        return;
      }

      if (activeLaserGuideModal) {
        closeLaserGuideModal();
        return;
      }

      if (activeFingerprintGuideModal) {
        closeFingerprintGuideModal();
        return;
      }

      if (activeAddonInfoModal) {
        closeAddonInfoModal();
        return;
      }

      if (isDrawerOpen()) {
        closeDrawer();
      }
    });

    addScopedEventListener(document, 'change', function (event) {
      var productForm = event.target.closest('form[action*="/cart/add"]');
      if (!productForm || section.contains(productForm)) return;

      if (getValidVariantIdFromForm(productForm)) {
        preferredProductForm = productForm;
      }

      scheduleMainProductSync(preferredProductForm || productForm);
    });

    ['variant:change', 'variantChange', 'product:variant-change'].forEach(function (eventName) {
      addScopedEventListener(document, eventName, function (event) {
        var variant = event.detail && (event.detail.variant || event.detail.currentVariant);

        if (variant && variant.id) {
          if (!findVariantById(variant.id)) return;

          section.setAttribute('data-product-variant-id', variant.id);
          syncMainProductFromActiveVariant(preferredProductForm);
          updateVariantPlateFields();
          updateFingerprintState();
          updateTotal();
          return;
        }

        scheduleMainProductSync(preferredProductForm);
      });
    });

    function getAvailableExtraCards() {
      return toArray(extraCards).filter(function (card) {
        return (
          card.getAttribute('data-extra-available') === 'true' &&
          !card.classList.contains('is-sold-out')
        );
      });
    }

    function getSelectedExtrasTotal() {
      return toArray(extraCards).reduce(function (sum, card) {
        if (!card.classList.contains('is-selected')) return sum;
        return sum + (Number(card.getAttribute('data-extra-price')) || 0);
      }, 0);
    }

    function getAvailableExtrasTotal() {
      return getAvailableExtraCards().reduce(function (sum, card) {
        return sum + (Number(card.getAttribute('data-extra-price')) || 0);
      }, 0);
    }

    function getExtrasBundleDiscountAmount() {
      if (!extrasBundleActive || getAvailableExtraCards().length < 2) return 0;

      var bundleSaving = extrasBundleCard ? Number(extrasBundleCard.getAttribute('data-extras-bundle-saving')) : 0;

      if (bundleSaving > 0) return bundleSaving;

      return Math.round(getAvailableExtrasTotal() / 2);
    }

    function updateExtrasBundleState() {
      if (!extrasBundleCard) return;

      extrasBundleCard.classList.toggle('is-selected', extrasBundleActive);
      extrasBundleCard.setAttribute('aria-pressed', extrasBundleActive ? 'true' : 'false');
    }

    function selectAllAvailableExtras(isSelected) {
      getAvailableExtraCards().forEach(function (card) {
        setSelectableCardState(card, isSelected);
      });
    }

    function toggleExtrasBundle() {
      if (!extrasBundleCard || getAvailableExtraCards().length < 2) return;

      extrasBundleActive = !extrasBundleActive;
      selectAllAvailableExtras(extrasBundleActive);
      updateExtrasBundleState();
      updateTotal();
    }

    function getExtraMessagePanel(card) {
      var item = card ? card.closest('[data-pa-extra-item]') : null;
      return item ? item.querySelector('[data-pa-extra-message-panel]') : null;
    }

    function getExtraMessageInput(card) {
      var panel = getExtraMessagePanel(card);
      return panel ? panel.querySelector('[data-pa-extra-message-input]') : null;
    }

    function updateExtraMessageCount(input) {
      var panel = input ? input.closest('[data-pa-extra-message-panel]') : null;
      var count = panel ? panel.querySelector('[data-pa-extra-message-count]') : null;

      if (count) {
        count.textContent = String((input.value || '').length);
      }
    }

    function setExtraMessageError(card, message, shouldScroll) {
      var panel = getExtraMessagePanel(card);

      if (!panel) return false;

      return setValidationState(
        panel,
        '[data-pa-extra-message-error]',
        '[data-pa-extra-message-error-text]',
        message,
        { scroll: !!shouldScroll, requireErrorElements: true }
      );
    }

    function setSelectableCardState(card, isSelected) {
      var wasSelected = card ? card.classList.contains('is-selected') : false;
      var messagePanel = getExtraMessagePanel(card);
      var messageItem = card ? card.closest('[data-pa-extra-item]') : null;
      var photoCardPanel = getAddonPhotoCardPanel(card);
      var photoCardItem = card ? card.closest('[data-pa-addon-item]') : null;

      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      if (messageItem) {
        messageItem.classList.toggle('is-selected', isSelected);
      }

      if (messagePanel) {
        messagePanel.hidden = !isSelected;

        if (!isSelected) {
          setExtraMessageError(card, '', false);
        }
      }

      if (photoCardItem) {
        photoCardItem.classList.toggle('is-selected', isSelected);
      }

      if (photoCardPanel) {
        photoCardPanel.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
        setElementInert(photoCardPanel, !isSelected);

        if (isSelected) {
          renderAddonPhotoCardOptions(photoCardPanel, !wasSelected && getMainPhotoUploadKitProperties().length > 0);
          syncAddonPhotoCardUploadVisibility(photoCardPanel);
          if (getAddonPhotoCardSelectedInput(photoCardPanel) && getAddonPhotoCardSelectedInput(photoCardPanel).getAttribute('data-photo-card-source') === 'new') {
            ensureAddonPhotoCardUploadkit(photoCardPanel);
          }
        } else {
          setAddonPhotoCardError(card, '', false);
        }
      }
    }

    function toggleAddonCard(card) {
      var isAvailable = card.getAttribute('data-addon-available') === 'true';
      if (!isAvailable) return;

      setSelectableCardState(card, !card.classList.contains('is-selected'));
      updateTotal();
    }

    function toggleExtraCard(card) {
      var isUnavailable =
        card.getAttribute('data-extra-available') === 'false' ||
        card.classList.contains('is-sold-out');

      if (isUnavailable) return;

      extrasBundleActive = false;
      setSelectableCardState(card, !card.classList.contains('is-selected'));
      updateExtrasBundleState();
      updateTotal();
    }

    function handleSelectableCardKeydown(card, toggleCallback) {
      return function (event) {
        if (event.target && event.target.closest('[data-pa-addon-info-open]')) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        toggleCallback(card);
      };
    }

    toArray(addonCards).forEach(function (card) {
      setSelectableCardState(card, card.classList.contains('is-selected'));
      addScopedEventListener(card, 'click', function (event) {
        if (event.target && event.target.closest('[data-pa-addon-info-open]')) return;
        toggleAddonCard(card);
      });
      addScopedEventListener(card, 'keydown', handleSelectableCardKeydown(card, toggleAddonCard));
    });

    toArray(section.querySelectorAll('[data-pa-addon-photo-card-panel]')).forEach(function (panel) {
      renderAddonPhotoCardOptions(panel);

      addScopedEventListener(panel, 'click', function (event) {
        var uploadTrigger = event.target && event.target.closest('[data-pa-photo-card-upload-trigger]');
        var option = event.target && event.target.closest('.pa-addon-photo-card-option');
        var input = option ? option.querySelector('[data-pa-photo-card-source]') : null;
        var card = panel.closest('[data-pa-addon-item]') ? panel.closest('[data-pa-addon-item]').querySelector('[data-pa-addon]') : null;

        if (!input || input.getAttribute('data-photo-card-source') !== 'new') return;

        if (uploadTrigger) {
          event.preventDefault();
          event.stopPropagation();
        }

        selectAddonPhotoCardNewUpload(panel);

        if (uploadTrigger) {
          openAddonPhotoCardUploadkitWhenReady(panel, card);
        }

        if (card && getAddonPhotoCardUploadControl(panel)) setAddonPhotoCardError(card, '', false);
      });

      addScopedEventListener(panel, 'keydown', function (event) {
        var option = event.target && event.target.closest('[data-pa-photo-card-new-option]');
        var card = panel.closest('[data-pa-addon-item]') ? panel.closest('[data-pa-addon-item]').querySelector('[data-pa-addon]') : null;

        if (!option || (event.key !== 'Enter' && event.key !== ' ')) return;

        event.preventDefault();
        selectAddonPhotoCardNewUpload(panel);
        if (card) setAddonPhotoCardError(card, '', false);
      });

      addScopedEventListener(panel, 'change', function (event) {
        var input = event.target && event.target.closest('[data-pa-photo-card-source]');
        var card = panel.closest('[data-pa-addon-item]') ? panel.closest('[data-pa-addon-item]').querySelector('[data-pa-addon]') : null;

        if (!input) return;

        syncAddonPhotoCardUploadVisibility(panel);
        updateAddonPhotoCardOptionStates(panel);

        if (input.getAttribute('data-photo-card-source') === 'new' && input.checked) {
          ensureAddonPhotoCardUploadkit(panel);
        }

        if (card) setAddonPhotoCardError(card, '', false);
      });

    });

    toArray(extraCards).forEach(function (card) {
      setSelectableCardState(card, card.classList.contains('is-selected'));
      addScopedEventListener(card, 'click', function () {
        toggleExtraCard(card);
      });
      addScopedEventListener(card, 'keydown', handleSelectableCardKeydown(card, toggleExtraCard));
    });

    qsa('[data-pa-extra-message-input]', section).forEach(function (input) {
      updateExtraMessageCount(input);

      addScopedEventListener(input, 'input', function () {
        var item = input.closest('[data-pa-extra-item]');
        var card = item ? item.querySelector('[data-pa-extra]') : null;

        updateExtraMessageCount(input);

        if (card && input.value.trim()) {
          setExtraMessageError(card, '', false);
        }
      });
    });

    if (extrasBundleCard) {
      updateExtrasBundleState();
      addScopedEventListener(extrasBundleCard, 'click', toggleExtrasBundle);
      addScopedEventListener(extrasBundleCard, 'keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        toggleExtrasBundle();
      });
    }

    function validateSelectedExtraMessages() {
      var isValid = true;

      toArray(section.querySelectorAll('[data-pa-extra].is-selected[data-extra-message-enabled="true"]')).some(function (card) {
        var input = getExtraMessageInput(card);
        var value = input ? input.value.trim() : '';

        if (value) {
          setExtraMessageError(card, '', false);
          return false;
        }

        setExtraMessageError(card, 'Escreva o texto que pretende incluir na carta.', true);

        if (input && typeof input.focus === 'function') {
          input.focus({ preventScroll: true });
        }

        isValid = false;
        return true;
      });

      return isValid;
    }

    function validateSelectedPhotoCardAddons() {
      var isValid = true;

      toArray(section.querySelectorAll('[data-pa-addon].is-selected[data-addon-photo-card-enabled="true"]')).some(function (card) {
        var panel = getAddonPhotoCardPanel(card);
        var selectedInput = getAddonPhotoCardSelectedInput(panel);
        var selectedProperty = getAddonPhotoCardSelectedProperty(panel);
        var source = selectedInput ? selectedInput.getAttribute('data-photo-card-source') : '';
        var message = source === 'new'
          ? 'Carregue a nova fotografia antes de adicionar ao carrinho.'
          : 'Selecione a fotografia que pretende usar no cartão.';

        if (selectedProperty && selectedProperty.name && selectedProperty.value) {
          setAddonPhotoCardError(card, '', false);
          return false;
        }

        renderAddonPhotoCardOptions(panel);
        setAddonPhotoCardError(card, message, true);

        isValid = false;
        return true;
      });

      return isValid;
    }

    /* ------------------------------------------------------------------------
      8. Upload validation, cart item building and submit flow
    ------------------------------------------------------------------------ */

    function clearUploadErrorIfPhotoExists() {
      var uploadkitBlock = section.querySelector('.pa-uploadkit-block');
      var uploadError = section.querySelector('[data-pa-upload-error]');

      if (!uploadkitBlock) return;

      var uploadkitType = uploadkitBlock.getAttribute('data-pa-uploadkit-block');

      if (uploadkitType === 'dual-micro-photo') {
        if (getDualMicroPhotoProperties().length < 2) return;
      } else {
        if (!hasUploadKitProperty(microPhotoUploadkit)) return;
      }

      uploadkitBlock.classList.remove('has-error');

      if (uploadError) {
        uploadError.hidden = true;
      }
    }

    function clearLaserPhotoErrorIfPhotoExists() {
      if (!laserPhotoUploadkit) return;
      if (!hasUploadKitProperty(laserPhotoUploadkit)) return;

      var laserPhotoField = laserBlock ? laserBlock.querySelector('[data-pa-laser-field="photo"]') : null;
      var laserError = laserBlock ? laserBlock.querySelector('[data-pa-laser-error]') : null;

      laserPhotoUploadkit.classList.remove('has-error');

      if (laserPhotoField) {
        laserPhotoField.classList.remove('has-error');

        var fieldError = laserPhotoField.querySelector('[data-pa-laser-field-error]');
        var fieldErrorText = laserPhotoField.querySelector('[data-pa-laser-field-error-text]');

        if (fieldError) {
          fieldError.hidden = true;
        }

        if (fieldErrorText) {
          fieldErrorText.textContent = '';
        }
      }

      if (laserError) {
        laserError.hidden = true;
      }
    }

    function refreshAddonPhotoCardPanels() {
      toArray(section.querySelectorAll('[data-pa-addon-photo-card-panel]')).forEach(function (panel) {
        var card = panel.closest('[data-pa-addon-item]') ? panel.closest('[data-pa-addon-item]').querySelector('[data-pa-addon]') : null;

        renderAddonPhotoCardOptions(panel);

        if (card && card.classList.contains('is-selected') && getAddonPhotoCardSelectedProperty(panel)) {
          setAddonPhotoCardError(card, '', false);
        }
      });
    }

    function syncDualMicroPhotoButtonCopy() {
      var button;
      var buttonCopy;

      if (!dualMicroPhotoUploadkit) return;

      button = dualMicroPhotoUploadkit.querySelector('.uploadkit-button');
      if (!button) return;

      buttonCopy = button.classList.contains('uploadkit-button-uploaded')
        ? 'Substituir Imagens'
        : 'Escolher Imagens';

      if (button.textContent.trim() !== buttonCopy) {
        button.textContent = buttonCopy;
      }
    }

    var microPhotoValidationRoots = [
      microPhotoUploadkit,
      dualMicroPhotoUploadkit,
    ].filter(Boolean);

    if (laserPhotoUploadkit) {
      var laserPhotoUploadkitObserver = new MutationObserver(function () {
        clearLaserPhotoErrorIfPhotoExists();
        refreshAddonPhotoCardPanels();
      });

      laserPhotoUploadkitObserver.observe(laserPhotoUploadkit, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value'],
      });
      registerMutationObserver(laserPhotoUploadkitObserver);

      addScopedEventListener(section, 'change', clearLaserPhotoErrorIfPhotoExists);
      addScopedEventListener(section, 'input', clearLaserPhotoErrorIfPhotoExists);
      addScopedEventListener(section, 'change', refreshAddonPhotoCardPanels);
      addScopedEventListener(section, 'input', refreshAddonPhotoCardPanels);
      addScopedEventListener(section, 'click', function () {
        setScopedTimeout(clearLaserPhotoErrorIfPhotoExists, 300);
        setScopedTimeout(clearLaserPhotoErrorIfPhotoExists, 900);
        setScopedTimeout(refreshAddonPhotoCardPanels, 300);
        setScopedTimeout(refreshAddonPhotoCardPanels, 900);
      });
    }

    if (microPhotoValidationRoots.length) {
      microPhotoValidationRoots.forEach(function (uploadkitRoot) {
        var uploadkitObserver = new MutationObserver(function () {
          clearUploadErrorIfPhotoExists();
          refreshAddonPhotoCardPanels();

          if (uploadkitRoot === dualMicroPhotoUploadkit) {
            syncDualMicroPhotoButtonCopy();
          }
        });

        uploadkitObserver.observe(uploadkitRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value'],
        });
        registerMutationObserver(uploadkitObserver);
      });

      syncDualMicroPhotoButtonCopy();

      addScopedEventListener(section, 'change', clearUploadErrorIfPhotoExists);
      addScopedEventListener(section, 'input', clearUploadErrorIfPhotoExists);
      addScopedEventListener(section, 'change', refreshAddonPhotoCardPanels);
      addScopedEventListener(section, 'input', refreshAddonPhotoCardPanels);
      addScopedEventListener(section, 'click', function () {
        setScopedTimeout(clearUploadErrorIfPhotoExists, 300);
        setScopedTimeout(clearUploadErrorIfPhotoExists, 900);
        setScopedTimeout(refreshAddonPhotoCardPanels, 300);
        setScopedTimeout(refreshAddonPhotoCardPanels, 900);
        setScopedTimeout(syncDualMicroPhotoButtonCopy, 300);
        setScopedTimeout(syncDualMicroPhotoButtonCopy, 900);
      });
    }

    qsa('[data-pa-uploadkit-root="photo-card"]', section).forEach(function (uploadkitRoot) {
      var photoCardObserver = new MutationObserver(function () {
        var panel = uploadkitRoot.closest('[data-pa-addon-photo-card-panel]');
        var card = panel && panel.closest('[data-pa-addon-item]') ? panel.closest('[data-pa-addon-item]').querySelector('[data-pa-addon]') : null;

        syncAddonPhotoCardNewOptionPreview(panel);

        if (card && card.classList.contains('is-selected') && getAddonPhotoCardSelectedProperty(panel)) {
          setAddonPhotoCardError(card, '', false);
        }
      });

      photoCardObserver.observe(uploadkitRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value'],
      });
      registerMutationObserver(photoCardObserver);
    });

    function clearSubmitError() {
      if (submitErrorText) {
        submitErrorText.textContent = '';
      }

      if (submitError) {
        submitError.hidden = true;
      }
    }

    function showSubmitError(message) {
      var finalMessage = message || 'Não foi possível adicionar ao carrinho. Confirme os dados e tente novamente.';

      if (!submitError || !submitErrorText) {
        console.warn('PA Drawer: erro de submissão:', finalMessage);
        return;
      }

      submitErrorText.textContent = finalMessage;
      submitError.hidden = false;
      scrollElementIntoView(submitError);
    }

    function openCartDrawer() {
      closeDrawer();

      document.dispatchEvent(new CustomEvent('cart:build'));

      if (window.theme && typeof window.theme.CartDrawer === 'function') {
        var cart = new window.theme.CartDrawer();

        if (typeof cart.init === 'function') {
          cart.init();
        }

        if (typeof cart.open === 'function') {
          cart.open();
          return;
        }
      }

      var cartDrawerTrigger =
        document.querySelector('[aria-controls="cart-drawer"]') ||
        document.querySelector('[aria-controls="CartDrawer"]') ||
        document.querySelector('[data-drawer-id="cart-drawer"]') ||
        document.querySelector('[data-drawer-id="CartDrawer"]') ||
        document.querySelector('[data-drawer-target="cart-drawer"]') ||
        document.querySelector('[data-drawer-target="CartDrawer"]') ||
        document.querySelector('[data-drawer-trigger="cart-drawer"]') ||
        document.querySelector('[data-drawer-trigger="CartDrawer"]') ||
        document.querySelector('[data-drawer-open="cart-drawer"]') ||
        document.querySelector('[data-drawer-open="CartDrawer"]') ||
        document.querySelector('.js-drawer-open-cart') ||
        document.querySelector('.site-header__cart');

      if (cartDrawerTrigger) {
        cartDrawerTrigger.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
        return;
      }

      console.warn('PA Drawer: product added, but the cart drawer did not open. Check the theme cart drawer trigger selectors.');
    }

    function resetUploadKitState() {
      /*
        Clear UploadKit's generated hidden properties and visual preview after a successful add-to-cart action.
        Uses explicit UploadKit roots so micro-photo and laser-photo are reset safely.
      */
      qsa(SELECTORS.uploadkitRoot, section).forEach(function (uploadkitRoot) {
        toArray(uploadkitRoot.querySelectorAll('input[name^="properties["]')).forEach(function (input) {
          input.remove();
        });

        toArray(uploadkitRoot.querySelectorAll('.uploadkit-thumbnails')).forEach(function (thumbnailWrapper) {
          thumbnailWrapper.innerHTML = '';
        });

        var initialTextField = uploadkitRoot.querySelector('.uploadkit-initial-text-field');
        if (initialTextField) {
          initialTextField.innerHTML = '';
        }

        var uploadButton = uploadkitRoot.querySelector('.uploadkit-button');
        if (uploadButton) {
          uploadButton.classList.remove('uploadkit-button-uploaded');
          uploadButton.textContent = uploadButtonDefaultText;
          uploadButton.removeAttribute('style');
        }
      });
    }

    function resetPersonalizationState() {
      toArray(addonCards).forEach(function (card) {
        setSelectableCardState(card, false);
      });

      extrasBundleActive = false;

      toArray(extraCards).forEach(function (card, index) {
        setSelectableCardState(card, !!initialExtraSelectionState[index]);
      });

      updateExtrasBundleState();

      toArray(section.querySelectorAll('[data-pa-property-toggle]')).forEach(function (toggle) {
        toggle.checked = false;
      });

      toArray(section.querySelectorAll('[data-pa-extra-property], [data-pa-uploadkit-property]')).forEach(function (input) {
        input.remove();
      });

      toArray(section.querySelectorAll('[data-pa-extra-message-input]')).forEach(function (input) {
        input.value = '';
        updateExtraMessageCount(input);
      });

      toArray(section.querySelectorAll('[data-pa-extra-message-panel]')).forEach(function (panel) {
        setValidationState(
          panel,
          '[data-pa-extra-message-error]',
          '[data-pa-extra-message-error-text]',
          '',
          { scroll: false, requireErrorElements: true }
        );
      });

      resetUploadKitState();
      resetLaserState();
      resetMorseState();
      resetFingerprintState();
      updateTotal();
    }

    function validateRequiredMicroPhoto() {
      var uploadkitBlock = section.querySelector('.pa-uploadkit-block');
      var uploadError = section.querySelector('[data-pa-upload-error]');

      if (!uploadkitBlock) return true;

      var uploadkitType = uploadkitBlock.getAttribute('data-pa-uploadkit-block');
      var isValid = true;

      if (uploadkitType === 'dual-micro-photo') {
        isValid = getDualMicroPhotoProperties().length >= 2;
      } else {
        isValid = hasUploadKitProperty(microPhotoUploadkit);
      }

      if (!isValid) {
        uploadkitBlock.classList.add('has-error');

        scrollElementIntoView(uploadkitBlock);

        if (uploadError) {
          uploadError.hidden = false;
        }

        return false;
      }

      uploadkitBlock.classList.remove('has-error');

      if (uploadError) {
        uploadError.hidden = true;
      }

      return true;
    }

    function collectMainUploadKitProperties(mainProperties) {
      if (dualMicroPhotoUploadkit) {
        var dualMicroPhotoProperties = getDualMicroPhotoProperties();

        dualMicroPhotoProperties.forEach(function (property) {
          mainProperties[property.name] = property.value;
        });

        return;
      }

      allUploadkitRoots.forEach(function (uploadkitRoot) {
        var uploadkitType = uploadkitRoot.getAttribute('data-pa-uploadkit-root');
        var uploadkitProperties = getUploadKitProperties(uploadkitRoot);

        uploadkitProperties.forEach(function (property) {
          if (uploadkitType === 'micro-photo') {
            mainProperties[property.name] = property.value;
          }
        });
      });
    }

    function addSelectedLinkedProducts(cartItems, mainProperties) {
      toArray(section.querySelectorAll('[data-pa-addon].is-selected')).forEach(function (card) {
        var variantId = card.getAttribute('data-addon-variant-id');
        var isAvailable = card.getAttribute('data-addon-available') === 'true';
        var addonProperties = {
          [PROPERTY_NAMES.mainItem]: mainProductTitle,
        };
        var photoCardPanel = getAddonPhotoCardPanel(card);
        var photoCardProperties = photoCardPanel ? getAddonPhotoCardSelectedProperties(photoCardPanel) : [];

        if (variantId && isAvailable) {
          photoCardProperties.forEach(function (photoCardProperty) {
            if (!photoCardProperty.name || !photoCardProperty.value) return;

            addonProperties[photoCardProperty.name] = photoCardProperty.value;
          });

          cartItems.push({
            id: Number(variantId),
            quantity: 1,
            properties: addonProperties,
          });
        }
      });

      toArray(section.querySelectorAll('[data-pa-extra].is-selected')).forEach(function (card) {
        var variantId = card.getAttribute('data-extra-variant-id');
        var isAvailable = card.getAttribute('data-extra-available') === 'true';
        var messageInput = getExtraMessageInput(card);
        var messageValue = messageInput ? messageInput.value.trim() : '';
        var messagePropertyName = messageInput ? messageInput.getAttribute('data-extra-message-property') : '';
        var extraProperties = {
          [PROPERTY_NAMES.mainItem]: mainProductTitle,
        };

        if (variantId && isAvailable) {
          if (messageValue) {
            extraProperties[messagePropertyName || PROPERTY_NAMES.handwrittenLetterMessage] = messageValue;
          }

          cartItems.push({
            id: Number(variantId),
            quantity: 1,
            properties: extraProperties,
          });
        }
      });

      if (laserBackCard) {
        var backToggle = laserBackCard.querySelector('[data-pa-laser-back-toggle]');
        var backInput = section.querySelector('[data-pa-laser-back-input]');
        var backValue = backInput ? backInput.value.trim() : '';
        var backVariantId = laserBackCard.getAttribute('data-laser-back-variant-id');
        var backAvailable = laserBackCard.getAttribute('data-laser-back-available') === 'true';

        if (backToggle && backToggle.checked && backVariantId && backAvailable) {
          var backProperties = {
            [PROPERTY_NAMES.mainItem]: mainProductTitle,
            [PROPERTY_NAMES.engravingBack]: backValue,
          };

          if (mainProperties && mainProperties._pa_link_id) {
            backProperties._pa_link_id = mainProperties._pa_link_id;
            backProperties._pa_role = 'laser-back';
          }

          cartItems.push({
            id: Number(backVariantId),
            quantity: 1,
            properties: backProperties,
          });
        }
      }

      addFingerprintKitProduct(cartItems);
    }

    function getCartSectionsToRender() {
      var cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        return cartDrawer.getSectionsToRender().map(function (sectionToRender) {
          return sectionToRender.id;
        });
      }

      return ['cart-drawer', 'cart-icon-bubble'];
    }

    function setSubmitLoading(isLoading) {
      isSubmitting = !!isLoading;

      if (!submitButton) return;

      submitButton.disabled = isSubmitting;
      submitButton.classList.toggle('is-loading', isSubmitting);
    }

    function parseCartError(errorData) {
      if (!errorData) return '';

      if (typeof errorData === 'string') return errorData;
      if (errorData.description) return String(errorData.description);
      if (errorData.message) return String(errorData.message);
      if (errorData.status && errorData.statusText) return errorData.status + ' ' + errorData.statusText;

      return '';
    }

    function addCartItems(cartItems) {
      return fetch(CART_ADD_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: cartItems,
          sections: getCartSectionsToRender(),
          sections_url: window.location.pathname,
        }),
      }).then(function (response) {
        if (!response.ok) {
          return response.json().then(function (errorData) {
            throw errorData;
          }).catch(function (errorData) {
            throw errorData || response;
          });
        }

        return response.json();
      });
    }

    function buildMainCartItem(variantId, properties) {
      return {
        id: Number(variantId),
        quantity: 1,
        properties: properties || {},
      };
    }

    function buildCartItems(mainVariantId, mainProperties) {
      var cartItems = [buildMainCartItem(mainVariantId, mainProperties)];

      addSelectedLinkedProducts(cartItems, mainProperties);

      return cartItems;
    }

    function handleSuccessfulCartAdd(options) {
      var settings = options || {};

      setSubmitLoading(false);
      clearSubmitError();

      if (settings.closeDrawer) {
        closeDrawer();
      }

      openCartDrawer();
      resetPersonalizationState();
    }

    function handleCartAddFailure(error, fallbackMessage, targetField) {
      var cartMessage = parseCartError(error);
      var message = fallbackMessage || 'Não foi possível adicionar ao carrinho. Confirme se a fotografia já terminou de carregar e tente novamente.';

      console.warn('PA Drawer: erro ao adicionar ao carrinho:', {
        error: error,
        cartMessage: cartMessage,
      });
      setSubmitLoading(false);

      if (targetField || isStarLaserSelected()) {
        showLaserError(message, targetField);
        return;
      }

      showSubmitError(message);
    }

    function collectPersonalizationProperties() {
      var mainProperties = {};
      var activeEngravingKey = section.getAttribute('data-pa-active-engraving') || '';

      collectMainUploadKitProperties(mainProperties);

      if (!collectLaserProperties(mainProperties)) return null;
      if (!collectLaserBackProperties(mainProperties)) return null;
      if (!collectMorseProperties(mainProperties)) return null;
      if (!collectFingerprintProperties(mainProperties)) return null;
      if (!validateSelectedExtraMessages()) return null;

      if (activeEngravingKey && engravingLabels[activeEngravingKey]) {
        mainProperties[PROPERTY_NAMES.engravingType] = engravingLabels[activeEngravingKey];
      }

      return mainProperties;
    }

    /* Main submit flow. Non-star products use /cart/add.js directly; star maps first call TeeInBlue. */
    function handleSubmit() {
      var mainVariantId;
      var activeVariant;
      var mainProperties;
      var cartItems;
      var starMapSubmitPromise;
      var starField;

      if (engravingSelector && !engravingSelector.hidden) {
        continueEngravingFlow();
        return;
      }

      if (isSubmitting) return;

      clearSubmitError();
      syncMainProductFromActiveVariant(preferredProductForm);

      mainVariantId = getActiveVariantIdFromPage(preferredProductForm);
      activeVariant = findVariantById(mainVariantId);

      if (activeVariant) {
        section.setAttribute('data-product-variant-id', activeVariant.id);
        syncMainProductFromActiveVariant(preferredProductForm);
        mainVariantId = section.getAttribute('data-product-variant-id');
      }

      mainProductTitle = section.getAttribute('data-product-full-title') || section.getAttribute('data-product-title') || '';

      if (!mainVariantId) {
        console.warn('PA Drawer: variante principal não encontrada na section.');
        showSubmitError('Não foi possível identificar a variante principal do produto.');
        return;
      }

      if (!validateRequiredMicroPhoto()) {
        return;
      }

      if (!validateSelectedPhotoCardAddons()) {
        return;
      }

      mainProperties = collectPersonalizationProperties();

      if (!mainProperties) {
        return;
      }

      setSubmitLoading(true);

      if (isStarLaserSelected()) {
        starField = laserBlock ? laserBlock.querySelector('[data-pa-laser-field="star"]') : null;
        starMapSubmitPromise = submitStarMapThroughTeeInBlue(mainProperties, mainVariantId);

        if (!starMapSubmitPromise) {
          setSubmitLoading(false);
          return;
        }

        starMapSubmitPromise
          .then(function () {
            handleSuccessfulCartAdd({ closeDrawer: true });
          })
          .catch(function (error) {
            handleCartAddFailure(
              error,
              'Não foi possível gerar o mapa das estrelas. Confirme os dados e tente novamente.',
              starField
            );
          });

        return;
      }

      cartItems = buildCartItems(mainVariantId, mainProperties);

      addCartItems(cartItems)
        .then(function () {
          handleSuccessfulCartAdd();
        })
        .catch(function (error) {
          handleCartAddFailure(error);
        });
    }

    if (submitButton) {
      addScopedEventListener(submitButton, 'click', handleSubmit);
    }

    updateTotal();
  }

  function findRootFromEventTarget(target) {
    if (!target) return null;

    if (typeof target.matches === 'function' && target.matches(ROOT_SELECTOR)) {
      return target;
    }

    if (typeof target.querySelector === 'function') {
      return target.querySelector(ROOT_SELECTOR);
    }

    return null;
  }

  function initAllPaPersonalizationDrawers() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);

    if (!roots.length) {
      console.warn('PA Drawer: nenhuma root encontrada.');
      return;
    }

    toArray(roots).forEach(initPaPersonalizationDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPaPersonalizationDrawers);
  } else {
    initAllPaPersonalizationDrawers();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = findRootFromEventTarget(event.target);

    if (root) {
      initPaPersonalizationDrawer(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = findRootFromEventTarget(event.target);

    if (root && typeof root.paDrawerDestroy === 'function') {
      root.paDrawerDestroy();
    }
  });
})();
